/**
 * Nariai Block Library — every reusable block used by the CMS + public renderer.
 * Each entry exports:
 *   Component  — React component with props: { props, tenant, site }
 *   defaultProps
 *   schema — form fields for the editor
 *   label  — display name in the CMS
 *
 * IMPORTANT: All blocks are 100% data-driven. Zero hardcoded tenant content.
 * Blocks are safe from infinite recursion — they never call BlockRenderer themselves.
 */

import {
  Pill, Truck, Stethoscope, ShieldCheck, Wrench, Hammer, Zap, ClipboardCheck,
  Briefcase, Users, Calendar, PartyPopper, Star, ArrowLeft, ChevronDown,
  ChevronLeft, ChevronRight, Phone, Mail, MapPin, Send, Play, Check,
  Award, Clock, DollarSign, ThumbsUp, Home, Car, Building, Scale,
  Scissors, Leaf, Flame, Snowflake, Droplet, Lightbulb, Brush, Layers,
  BadgeCheck, HardHat, Sparkles, Heart, Cross, Download, Printer, Bookmark,
  Crosshair,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { CalculatorBlock } from "./CalculatorBlock";
import HeroChat from "@/pages/HeroChat";

// Icon registry (used by services grid + trust badges + why choose us + process)
const ICONS = {
  Pill, Truck, Stethoscope, ShieldCheck, Wrench, Hammer, Zap, ClipboardCheck,
  Briefcase, Users, Calendar, PartyPopper, Star, ArrowLeft, Phone, Mail, MapPin,
  Check, Award, Clock, DollarSign, ThumbsUp, Home, Car, Building, Scale,
  Scissors, Leaf, Flame, Snowflake, Droplet, Lightbulb, Brush, Layers,
  BadgeCheck, HardHat, Sparkles, Heart, Cross, Download,
};
const Icon = ({ name, ...rest }) => {
  const C = ICONS[name] || Star;
  return <C {...rest} />;
};

// Auto-choose readable button text color from a background hex.
// Returns "#0D0D0D" for light backgrounds (like #FFC107 yellow) or "#FFFFFF" for dark.
function readableTextOn(hex) {
  if (!hex || typeof hex !== "string") return "#FFFFFF";
  const h = hex.replace("#", "");
  const n = h.length === 3
    ? h.split("").map((c) => parseInt(c + c, 16))
    : [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
  const [r, g, b] = n.map((c) => (c / 255));
  // Relative luminance per WCAG
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.55 ? "#0D0D0D" : "#FFFFFF";
}

// ============================================================
// HERO
// ============================================================
// ============================================================
// AWARDS RIBBON — floating hero overlay + full-page gallery block
// ============================================================
function AwardsRibbon({ awards, tenant }) {
  const [expanded, setExpanded] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [mounted, setMounted] = useState(false);
  const brand = tenant?.primary_color || "#FFC107";
  const label = awards.label || "Award-Winning Commercial Contractor";
  const items = awards.awards || [];
  const yearsSpan = items.length > 0
    ? `${items[0].year}–${items[items.length - 1].year}`
    : "";

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="absolute z-20 top-6 right-6 md:top-8 md:right-8"
      style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(-12px)",
        transition: "opacity 700ms ease-out, transform 700ms ease-out",
        maxWidth: "min(28vw, 460px)",
      }}
      data-testid="hero-awards-ribbon"
    >
      {/* DESKTOP full timeline */}
      <div
        className="hidden md:block relative rounded-2xl px-5 py-4"
        style={{
          background: "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(20,15,0,0.75) 100%)",
          border: `1px solid ${brand}55`,
          boxShadow: `0 20px 40px -20px ${brand}66, 0 0 60px -20px ${brand}44, inset 0 1px 0 rgba(255,255,255,0.06)`,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: brand, flexShrink: 0 }}>
            <path d="M8 21h8m-4-4v4m-7-8a5 5 0 005 5h4a5 5 0 005-5V4H5v9z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5 6H3v2a3 3 0 003 3M19 6h2v2a3 3 0 01-3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <div style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "0.68rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: brand,
            fontWeight: 600,
          }}>{label}</div>
        </div>
        <div className="flex flex-wrap gap-x-2 gap-y-1 items-center relative">
          {items.map((a, i) => (
            <div key={a.year} className="relative" onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
              <span
                className="cursor-help"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "1.05rem",
                  fontWeight: 500,
                  color: hoveredIdx === i ? brand : "#FFF",
                  letterSpacing: "0.02em",
                  transition: "color 200ms",
                }}
              >{a.year}</span>
              {i < items.length - 1 && (
                <span style={{ color: `${brand}66`, margin: "0 0.15rem", fontSize: "0.75rem" }}>•</span>
              )}
              {hoveredIdx === i && (
                <div
                  className="absolute z-30 mt-2 rounded-xl p-4"
                  data-testid="hero-awards-tooltip"
                  style={{
                    top: "100%",
                    right: 0,
                    width: 260,
                    background: "rgba(15,15,15,0.98)",
                    border: `1px solid ${brand}66`,
                    boxShadow: `0 20px 50px -10px ${brand}55, 0 8px 20px -5px rgba(0,0,0,0.8)`,
                  }}
                >
                  <div style={{
                    fontFamily: "Outfit, sans-serif",
                    fontSize: "0.65rem",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: brand,
                    marginBottom: 4,
                  }}>{a.year} {a.issuer ? `· ${a.issuer}` : ""}</div>
                  <div style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontSize: "1.15rem",
                    fontWeight: 600,
                    color: "#FFF",
                    marginBottom: 6,
                    lineHeight: 1.15,
                  }}>{a.name}</div>
                  {a.description && (
                    <div style={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "0.82rem",
                      color: "#D4D4D4",
                      lineHeight: 1.45,
                    }}>{a.description}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {awards.link_href && (
          <a
            href={awards.link_href}
            data-testid="hero-awards-see-all"
            style={{
              display: "inline-block",
              marginTop: "0.5rem",
              fontFamily: "Outfit, sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: brand,
              textDecoration: "none",
              opacity: 0.85,
            }}
          >See all awards →</a>
        )}
      </div>

      {/* MOBILE compact badge */}
      <button
        type="button"
        className="md:hidden flex items-center gap-2 rounded-full px-4 py-2"
        style={{
          background: "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(20,15,0,0.75) 100%)",
          border: `1px solid ${brand}66`,
          boxShadow: `0 8px 20px -5px ${brand}55`,
          color: brand,
          fontFamily: "Outfit, sans-serif",
          fontSize: "0.7rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
        onClick={() => setExpanded(v => !v)}
        data-testid="hero-awards-mobile-toggle"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M8 21h8m-4-4v4m-7-8a5 5 0 005 5h4a5 5 0 005-5V4H5v9z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Award Winning · {yearsSpan}</span>
      </button>
      {expanded && (
        <div
          className="md:hidden fixed z-40 rounded-2xl px-4 py-4 shadow-2xl"
          data-testid="hero-awards-mobile-panel"
          style={{
            top: 88,
            left: 12,
            right: 12,
            maxHeight: "70vh",
            overflowY: "auto",
            background: "rgba(10,10,10,0.98)",
            border: `1px solid ${brand}66`,
            boxShadow: `0 30px 60px -10px ${brand}66, 0 20px 40px -10px rgba(0,0,0,0.9)`,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div style={{
              fontFamily: "Outfit, sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: brand,
              fontWeight: 600,
            }}>{label}</div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              style={{
                background: "transparent",
                border: "none",
                color: brand,
                fontSize: "1.25rem",
                lineHeight: 1,
                cursor: "pointer",
                padding: 4,
              }}
              aria-label="Close"
              data-testid="hero-awards-mobile-close"
            >×</button>
          </div>
          <ul className="space-y-2.5">
            {items.map(a => (
              <li key={a.year}>
                <div style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "1.05rem",
                  color: brand,
                  fontWeight: 600,
                }}>{a.year} — {a.name}</div>
                {a.description && (
                  <div style={{
                    fontFamily: "Outfit, sans-serif",
                    fontSize: "0.78rem",
                    color: "#D4D4D4",
                    lineHeight: 1.4,
                  }}>{a.description}</div>
                )}
              </li>
            ))}
          </ul>
          {awards.link_href && (
            <a
              href={awards.link_href}
              className="block mt-3 pt-3 border-t"
              style={{
                borderColor: `${brand}33`,
                fontFamily: "Outfit, sans-serif",
                fontSize: "0.75rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: brand,
                textDecoration: "none",
              }}
            >See all awards →</a>
          )}
        </div>
      )}
    </div>
  );
}


// ---- Integrated Awards strip along the bottom of the cinematic hero ----
function HeroAwardsStrip({ awards, tenant }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const brand = tenant?.primary_color || "#FFC107";
  const items = awards.awards || [];
  const label = awards.label || "Award-Winning Commercial Contractor";
  const yearsSpan = items.length > 0
    ? `${items[0].year}–${items[items.length - 1].year}`
    : "";

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 320);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* Desktop — full-bleed thin architectural line at the very bottom edge of the hero */}
      <div
        className="hidden md:block absolute left-0 right-0 bottom-0 z-20"
        data-testid="hero-awards-ribbon"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 900ms ease-out 200ms, transform 900ms ease-out 200ms",
          borderTop: `1px solid ${brand}33`,
          background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.85) 100%)",
        }}
      >
        <div
          className="flex items-center justify-between gap-6"
          style={{
            paddingLeft: "clamp(1.5rem, 5vw, 6rem)",
            paddingRight: "clamp(1.5rem, 5vw, 6rem)",
            paddingTop: "1.1rem",
            paddingBottom: "1.1rem",
          }}
        >
          <div className="flex items-center gap-4">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ color: brand, flexShrink: 0 }}>
              <path d="M8 21h8m-4-4v4m-7-8a5 5 0 005 5h4a5 5 0 005-5V4H5v9z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 6H3v2a3 3 0 003 3M19 6h2v2a3 3 0 01-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            <div>
              <div style={{
                fontFamily: "Outfit, sans-serif",
                fontSize: "0.62rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: brand,
                fontWeight: 600,
              }}>{label}</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-1.5" data-testid="hero-awards-years">
                {items.map((a, i) => (
                  <span key={a.year} className="relative" onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
                    <span
                      className="cursor-help"
                      style={{
                        fontFamily: "Cormorant Garamond, serif",
                        fontSize: "1.05rem",
                        fontWeight: 500,
                        color: hoveredIdx === i ? brand : "rgba(255,255,255,0.92)",
                        letterSpacing: "0.02em",
                        transition: "color 200ms",
                      }}
                    >{a.year}</span>
                    {i < items.length - 1 && (
                      <span style={{ color: `${brand}55`, marginLeft: "0.3rem", marginRight: "0.15rem", fontSize: "0.75rem" }}>·</span>
                    )}
                    {hoveredIdx === i && (
                      <div
                        className="absolute z-30 rounded-xl p-4"
                        data-testid="hero-awards-tooltip"
                        style={{
                          bottom: "calc(100% + 12px)",
                          left: 0,
                          width: 280,
                          background: "rgba(12,12,12,0.98)",
                          border: `1px solid ${brand}66`,
                          boxShadow: `0 20px 50px -10px ${brand}55, 0 8px 20px -5px rgba(0,0,0,0.85)`,
                        }}
                      >
                        <div style={{
                          fontFamily: "Outfit, sans-serif",
                          fontSize: "0.65rem",
                          letterSpacing: "0.2em",
                          textTransform: "uppercase",
                          color: brand,
                          marginBottom: 4,
                        }}>{a.year} {a.issuer ? `· ${a.issuer}` : ""}</div>
                        <div style={{
                          fontFamily: "Cormorant Garamond, serif",
                          fontSize: "1.15rem",
                          fontWeight: 600,
                          color: "#FFF",
                          marginBottom: 6,
                          lineHeight: 1.15,
                        }}>{a.name}</div>
                        {a.description && (
                          <div style={{
                            fontFamily: "Outfit, sans-serif",
                            fontSize: "0.8rem",
                            color: "#D4D4D4",
                            lineHeight: 1.45,
                          }}>{a.description}</div>
                        )}
                      </div>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {awards.link_href && (
            <a
              href={awards.link_href}
              data-testid="hero-awards-see-all"
              className="hidden md:inline-block whitespace-nowrap"
              style={{
                fontFamily: "Outfit, sans-serif",
                fontSize: "0.7rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: brand,
                textDecoration: "none",
                padding: "0.5rem 1rem",
                border: `1px solid ${brand}66`,
                borderRadius: 999,
                fontWeight: 600,
              }}
            >See all awards →</a>
          )}
        </div>
      </div>

      {/* Mobile — compact pill in upper-right; tap opens fixed overlay */}
      <button
        type="button"
        className="md:hidden absolute z-20 top-4 right-4 flex items-center gap-2 rounded-full px-3 py-1.5"
        style={{
          background: "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(20,15,0,0.8) 100%)",
          border: `1px solid ${brand}66`,
          boxShadow: `0 8px 20px -5px ${brand}55`,
          color: brand,
          fontFamily: "Outfit, sans-serif",
          fontSize: "0.62rem",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          fontWeight: 600,
          opacity: mounted ? 1 : 0,
          transition: "opacity 700ms ease-out 200ms",
        }}
        onClick={() => setMobileOpen(v => !v)}
        data-testid="hero-awards-mobile-toggle"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M8 21h8m-4-4v4m-7-8a5 5 0 005 5h4a5 5 0 005-5V4H5v9z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Award Winning · {yearsSpan}</span>
      </button>
      {mobileOpen && (
        <div
          className="md:hidden fixed z-40 rounded-2xl px-4 py-4"
          data-testid="hero-awards-mobile-panel"
          style={{
            top: 90,
            left: 12,
            right: 12,
            maxHeight: "70vh",
            overflowY: "auto",
            background: "rgba(10,10,10,0.98)",
            border: `1px solid ${brand}66`,
            boxShadow: `0 30px 60px -10px ${brand}66, 0 20px 40px -10px rgba(0,0,0,0.9)`,
            backdropFilter: "blur(12px)",
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div style={{
              fontFamily: "Outfit, sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: brand,
              fontWeight: 600,
            }}>{label}</div>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              style={{ background: "transparent", border: "none", color: brand, fontSize: "1.25rem", cursor: "pointer", padding: 4 }}
              aria-label="Close"
              data-testid="hero-awards-mobile-close"
            >×</button>
          </div>
          <ul className="space-y-2.5">
            {items.map(a => (
              <li key={a.year}>
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem", color: brand, fontWeight: 600 }}>{a.year} — {a.name}</div>
                {a.description && (
                  <div style={{ fontFamily: "Outfit, sans-serif", fontSize: "0.78rem", color: "#D4D4D4", lineHeight: 1.4 }}>{a.description}</div>
                )}
              </li>
            ))}
          </ul>
          {awards.link_href && (
            <a href={awards.link_href} className="block mt-3 pt-3 border-t" style={{ borderColor: `${brand}33`, fontFamily: "Outfit, sans-serif", fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", color: brand, textDecoration: "none" }}>See all awards →</a>
          )}
        </div>
      )}
    </>
  );
}



function AwardsGalleryBlock({ props, tenant }) {
  const brand = tenant?.primary_color || "#FFC107";
  const items = props.items || [];
  return (
    <section className="w-full py-16 md:py-24" style={{ background: "#0A0A0A" }}>
      <div className="max-w-6xl mx-auto px-6">
        {props.eyebrow && (
          <div style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "0.75rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: brand,
            marginBottom: "0.75rem",
          }}>{props.eyebrow}</div>
        )}
        {props.headline && (
          <h2 style={{
            fontFamily: "Cormorant Garamond, serif",
            fontSize: "clamp(2rem, 4vw, 3.5rem)",
            fontWeight: 600,
            color: "#FFF",
            lineHeight: 1.1,
            marginBottom: "1rem",
          }}>{props.headline}</h2>
        )}
        {props.subheadline && (
          <p className="max-w-3xl mb-12" style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "1.1rem",
            color: "#D4D4D4",
            lineHeight: 1.6,
          }}>{props.subheadline}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(a => (
            <div
              key={a.year}
              data-testid={`awards-gallery-card-${a.year}`}
              className="rounded-2xl p-6"
              style={{
                background: "linear-gradient(180deg, rgba(255,193,7,0.05) 0%, rgba(0,0,0,0) 100%)",
                border: `1px solid ${brand}33`,
                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04)`,
              }}
            >
              {a.image_url ? (
                <img
                  src={a.image_url}
                  alt={`${a.name} ${a.year}`}
                  className="w-full aspect-video object-cover rounded-lg mb-4"
                  style={{ background: "#0A0A0A" }}
                />
              ) : (
                <div
                  className="w-full aspect-video rounded-lg mb-4 flex flex-col items-center justify-center"
                  style={{
                    background: "linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(30,20,0,0.85) 100%)",
                    border: `1px solid ${brand}22`,
                  }}
                >
                  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" style={{ color: brand }}>
                    <path d="M8 21h8m-4-4v4m-7-8a5 5 0 005 5h4a5 5 0 005-5V4H5v9z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M5 6H3v2a3 3 0 003 3M19 6h2v2a3 3 0 01-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <div style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontSize: "2.5rem",
                    fontWeight: 600,
                    color: brand,
                    marginTop: "0.5rem",
                  }}>{a.year}</div>
                </div>
              )}
              <div style={{
                fontFamily: "Outfit, sans-serif",
                fontSize: "0.72rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: brand,
                marginBottom: "0.35rem",
              }}>{a.year}{a.issuer ? ` · ${a.issuer}` : ""}</div>
              <h3 style={{
                fontFamily: "Cormorant Garamond, serif",
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "#FFF",
                marginBottom: "0.5rem",
                lineHeight: 1.15,
              }}>{a.name}</h3>
              {a.description && (
                <p style={{
                  fontFamily: "Outfit, sans-serif",
                  fontSize: "0.95rem",
                  color: "#D4D4D4",
                  lineHeight: 1.55,
                }}>{a.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const AwardsGalleryDef = {
  Component: AwardsGalleryBlock,
  label: "Awards Gallery",
  defaultProps: {
    eyebrow: "Recognition",
    headline: "Awards & Recognition",
    subheadline: "",
    items: [],
  },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "items", label: "Awards (year, name, issuer, description, image_url)", type: "json" },
  ],
};



// ------------ Inner-page header helpers ------------
// Classifies which header variant a page should use so the homepage full-bleed
// carousel never leaks onto interior pages.
export function classifyPageKind(slug) {
  if (!slug || slug === "/") return "home";
  if (slug.startsWith("/learn/") && slug.split("/").length > 2 && slug !== "/learn/") return "article";
  if (slug === "/learn") return "hub";
  if (slug.startsWith("/tools/") || slug === "/tools") return "tool";
  if (slug === "/privacy" || slug === "/terms") return "legal";
  if (slug === "/contact") return "contact";
  if (slug === "/404") return "legal";
  return "inner";
}

function buildBreadcrumbs(slug, tenant) {
  if (!slug || slug === "/") return [];
  const parts = slug.split("/").filter(Boolean);
  const crumbs = [{ label: "Home", href: "/" }];
  let acc = "";
  parts.forEach((p, i) => {
    acc += "/" + p;
    const label = p
      .replace(/-/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());
    crumbs.push({ label, href: i === parts.length - 1 ? null : acc });
  });
  return crumbs;
}

// Reusable laurel-wreath award badge — rendered as inline SVG so it floats
// against the marble without any container or glass. Sized entirely by prop.
function LaurelAward({ year, size = 96 }) {
  const w = size;
  const h = Math.round(size * 1.35);
  return (
    <div
      className="flex flex-col items-center"
      style={{ width: w }}
      data-testid={`award-wreath-${year}`}
    >
      <svg
        viewBox="0 0 100 100"
        width={w}
        height={w}
        aria-hidden
        style={{ filter: "drop-shadow(0 8px 22px rgba(0,0,0,0.7))" }}
      >
        {/* Left laurel */}
        <g fill="#F1EBD8" opacity="0.95">
          {[10, 22, 34, 46, 58, 70, 82].map((cy, i) => (
            <ellipse
              key={`l${i}`}
              cx={22}
              cy={cy}
              rx={5}
              ry={3.2}
              transform={`rotate(${-30 - i * 4} 22 ${cy})`}
            />
          ))}
        </g>
        {/* Right laurel (mirror) */}
        <g fill="#F1EBD8" opacity="0.95">
          {[10, 22, 34, 46, 58, 70, 82].map((cy, i) => (
            <ellipse
              key={`r${i}`}
              cx={78}
              cy={cy}
              rx={5}
              ry={3.2}
              transform={`rotate(${30 + i * 4} 78 ${cy})`}
            />
          ))}
        </g>
        {/* Trophy cup */}
        <g fill="#F1EBD8">
          <path d="M35 24 L65 24 L64 42 C64 52 58 58 50 58 C42 58 36 52 36 42 Z" />
          <path d="M35 28 C 22 28 22 42 32 44 L34 40 C 28 38 28 32 35 32 Z" />
          <path d="M65 28 C 78 28 78 42 68 44 L66 40 C 72 38 72 32 65 32 Z" />
          <rect x="45" y="58" width="10" height="6" />
          <rect x="40" y="64" width="20" height="4" rx="1" />
          {/* Star inside cup */}
          <polygon
            points="50,32 52,38 58,38 53,42 55,48 50,45 45,48 47,42 42,38 48,38"
            fill="#C9A24B"
          />
        </g>
      </svg>
      <div
        className="uppercase text-center"
        style={{
          color: "#F1EBD8",
          fontSize: Math.max(6, Math.round(size * 0.075)) + "px",
          letterSpacing: "0.08em",
          fontWeight: 700,
          lineHeight: 1.15,
          marginTop: 2,
          textShadow: "0 2px 8px rgba(0,0,0,0.85)",
        }}
      >
        Best Companies
        <div style={{ opacity: 0.85, fontSize: Math.max(5, Math.round(size * 0.06)) + "px", letterSpacing: "0.12em" }}>to Work For</div>
      </div>
      <div
        className="font-cormorant"
        style={{
          color: "#E5C77A",
          fontSize: Math.max(14, Math.round(size * 0.22)) + "px",
          fontWeight: 400,
          lineHeight: 1,
          marginTop: 4,
          textShadow: "0 4px 14px rgba(229,199,122,0.35), 0 2px 6px rgba(0,0,0,0.7)",
          letterSpacing: "0.02em",
        }}
      >
        {year}
      </div>
    </div>
  );
}

function Breadcrumbs({ items, brand }) {
  if (!items || items.length === 0) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="breadcrumbs"
      className="font-outfit uppercase text-[11px] tracking-[0.2em]"
      style={{ color: "rgba(255,255,255,0.6)" }}
    >
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {items.map((c, i) => (
          <li key={i} className="flex items-center gap-2">
            {c.href ? (
              <a href={c.href} className="hover:opacity-80" style={{ color: "rgba(255,255,255,0.72)" }}>
                {c.label}
              </a>
            ) : (
              <span style={{ color: brand }}>{c.label}</span>
            )}
            {i < items.length - 1 && (
              <span aria-hidden style={{ color: "rgba(255,255,255,0.35)" }}>/</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Compact interior banner (280–420px desktop, 180–240px mobile) used by
// services / industries / locations / projects / about / hub-index pages.
function InnerPageHeader({ props, tenant, page }) {
  const { eyebrow, headline, subheadline, cta_text, cta_href, cta2_text, cta2_href, image_url, floating_awards } = props || {};
  const brand = "#E5C77A"; // premium gold
  const bg = image_url && image_url !== "/lone-star-hero-bg-2k.jpg" ? image_url : null;
  const crumbs = buildBreadcrumbs(page?.slug, tenant);
  const awardsList = Array.isArray(floating_awards) ? floating_awards : [];
  return (
    <section
      className={`relative overflow-hidden tenant-theme-dark ${bg ? "" : "marble-surface"}`}
      data-block="hero"
      data-variant="inner"
      data-testid="inner-page-header"
      style={{
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        minHeight: awardsList.length > 0 ? "clamp(340px, 44vw, 560px)" : "clamp(240px, 34vw, 400px)",
        backgroundImage: bg
          ? `linear-gradient(180deg, rgba(10,10,12,0.72), rgba(10,10,12,0.82)), url(${bg})`
          : "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.28) 55%, rgba(0,0,0,0.65) 100%), url(/marble-nav-1600.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        borderBottom: "1px solid rgba(229,199,122,0.22)",
      }}
    >
      {/* Gold hairline top edge */}
      <div aria-hidden className="absolute top-0 left-0 right-0 gold-hairline" />
      {/* Vertical gold accent on the far left */}
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0"
        style={{ width: 2, background: `linear-gradient(180deg, ${brand}, transparent)` }}
      />
      <div
        className="relative z-10 flex flex-col justify-end h-full"
        style={{
          minHeight: "clamp(240px, 34vw, 400px)",
          paddingLeft: "clamp(1.25rem, 5vw, 5rem)",
          paddingRight: "clamp(1.25rem, 5vw, 5rem)",
          paddingTop: "clamp(3rem, 8vw, 5rem)",
          paddingBottom: "clamp(1.25rem, 3vw, 2.25rem)",
        }}
      >
        <Breadcrumbs items={crumbs} brand={brand} />
        {eyebrow && (
          <div
            className="font-outfit uppercase mt-4 mb-3 flex items-center gap-3"
            style={{ color: brand, fontSize: "0.7rem", letterSpacing: "0.28em", fontWeight: 700 }}
          >
            <span aria-hidden style={{ display: "inline-block", width: 32, height: 1, background: `linear-gradient(90deg, transparent, ${brand})` }} />
            {eyebrow}
          </div>
        )}
        <h1
          className="font-cab font-black text-white uppercase"
          style={{
            fontSize: "clamp(1.8rem, 3.6vw, 3.4rem)",
            lineHeight: 1.04,
            letterSpacing: "-0.005em",
            textShadow: "0 4px 20px rgba(0,0,0,0.7)",
            maxWidth: "900px",
          }}
        >
          {headline}
        </h1>
        {subheadline && (
          <p
            className="font-outfit mt-3 max-w-2xl"
            style={{ fontSize: "clamp(0.9rem, 1.05vw, 1.05rem)", color: "rgba(240,240,238,0.88)", lineHeight: 1.5 }}
          >
            {subheadline}
          </p>
        )}
        {(cta_text || cta2_text) && (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {cta_text && (
              <a href={cta_href || "#contact"} className="gold-cta text-xs" data-testid="inner-header-cta">
                {cta_text} <span aria-hidden>★</span>
              </a>
            )}
            {cta2_text && (
              <a
                href={cta2_href || "#contact"}
                className="inline-flex items-center gap-2 px-5 py-2 text-xs font-semibold tracking-[0.18em] uppercase rounded-full border border-white/40 text-white/90 hover:text-white hover:border-white transition-colors"
                data-testid="inner-header-cta-2"
              >
                {cta2_text}
              </a>
            )}
          </div>
        )}
      </div>
      {/* Floating awards overlay — no container, no glass, just wreaths on the marble */}
      {awardsList.length > 0 && (
        <div
          className="pointer-events-none absolute z-[5] hidden lg:grid grid-cols-4 gap-x-6 gap-y-8"
          style={{
            right: "clamp(1.5rem, 4vw, 5rem)",
            top: "50%",
            transform: "translateY(-50%)",
            width: "min(52%, 560px)",
            justifyItems: "center",
          }}
          data-testid="floating-awards-overlay"
        >
          {/* Soft golden bokeh glow underneath — floating, not a container */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 50% 55%, rgba(229,199,122,0.18) 0%, rgba(201,162,75,0.08) 40%, transparent 75%)",
              filter: "blur(36px)",
              zIndex: -1,
              transform: "scale(1.25)",
            }}
          />
          {awardsList.map((a) => (
            <LaurelAward key={a.year} year={a.year} size={a.size || 100} />
          ))}
        </div>
      )}
      {/* Mobile floating strip — smaller wreaths, wrap under the copy */}
      {awardsList.length > 0 && (
        <div
          className="lg:hidden flex flex-wrap justify-center gap-x-4 gap-y-4 mt-6"
          style={{
            paddingLeft: "clamp(1.25rem, 5vw, 5rem)",
            paddingRight: "clamp(1.25rem, 5vw, 5rem)",
            paddingBottom: "1.5rem",
          }}
        >
          {awardsList.map((a) => (
            <LaurelAward key={a.year + "m"} year={a.year} size={62} />
          ))}
        </div>
      )}
    </section>
  );
}

// Ultra-compact header for learn articles — breadcrumbs + category + title.
function ArticleHeader({ props, tenant, page }) {
  const { eyebrow, headline, subheadline } = props || {};
  const brand = tenant?.primary_color || "#FFC107";
  const crumbs = buildBreadcrumbs(page?.slug, tenant);
  return (
    <section
      className="relative overflow-hidden tenant-theme-dark"
      data-block="hero"
      data-variant="article"
      data-testid="article-header"
      style={{
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        background: "linear-gradient(180deg, #141414 0%, #0D0D0D 100%)",
      }}
    >
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0"
        style={{ width: 3, background: `linear-gradient(180deg, ${brand}, ${brand}00)` }}
      />
      <div
        className="relative z-10"
        style={{
          paddingLeft: "clamp(1.25rem, 5vw, 5rem)",
          paddingRight: "clamp(1.25rem, 5vw, 5rem)",
          paddingTop: "clamp(3rem, 6vw, 4rem)",
          paddingBottom: "clamp(1.25rem, 2.5vw, 2rem)",
        }}
      >
        <Breadcrumbs items={crumbs} brand={brand} />
        {eyebrow && (
          <div
            className="font-outfit uppercase mt-4 mb-2"
            style={{ color: brand, fontSize: "0.7rem", letterSpacing: "0.24em", fontWeight: 600 }}
          >
            {eyebrow}
          </div>
        )}
        <h1
          className="font-cormorant font-semibold text-white"
          style={{
            fontSize: "clamp(1.6rem, 3vw, 2.75rem)",
            lineHeight: 1.08,
            letterSpacing: "-0.012em",
            maxWidth: "72ch",
          }}
        >
          {headline}
        </h1>
        {subheadline && (
          <p
            className="font-outfit mt-3 max-w-3xl"
            style={{ fontSize: "clamp(0.9rem, 1vw, 1rem)", color: "rgba(220,220,220,0.85)", lineHeight: 1.55 }}
          >
            {subheadline}
          </p>
        )}
      </div>
    </section>
  );
}

// Short title band for tool/calculator pages.
function ToolHeader({ props, tenant, page }) {
  const { eyebrow, headline } = props || {};
  const brand = tenant?.primary_color || "#FFC107";
  const crumbs = buildBreadcrumbs(page?.slug, tenant);
  return (
    <section
      className="relative overflow-hidden tenant-theme-dark"
      data-block="hero"
      data-variant="tool"
      data-testid="tool-header"
      style={{
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        background: "#0D0D0D",
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
      }}
    >
      <div
        className="relative z-10"
        style={{
          paddingLeft: "clamp(1.25rem, 5vw, 5rem)",
          paddingRight: "clamp(1.25rem, 5vw, 5rem)",
          paddingTop: "clamp(2.5rem, 5vw, 3.5rem)",
          paddingBottom: "clamp(1rem, 2vw, 1.5rem)",
        }}
      >
        <Breadcrumbs items={crumbs} brand={brand} />
        {eyebrow && (
          <div
            className="font-outfit uppercase mt-3 mb-1"
            style={{ color: brand, fontSize: "0.7rem", letterSpacing: "0.24em", fontWeight: 600 }}
          >
            {eyebrow}
          </div>
        )}
        <h1
          className="font-cormorant font-semibold text-white"
          style={{
            fontSize: "clamp(1.6rem, 2.6vw, 2.5rem)",
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
          }}
        >
          {headline}
        </h1>
      </div>
    </section>
  );
}

// Simple branded title band for legal / contact pages — no photography.
function LegalHeader({ props, tenant, page }) {
  const { headline, subheadline } = props || {};
  const brand = tenant?.primary_color || "#FFC107";
  const crumbs = buildBreadcrumbs(page?.slug, tenant);
  return (
    <section
      className="relative overflow-hidden tenant-theme-dark"
      data-block="hero"
      data-variant="legal"
      data-testid="legal-header"
      style={{
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        background: "#0D0D0D",
      }}
    >
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0"
        style={{ width: 2, background: brand, opacity: 0.7 }}
      />
      <div
        className="relative z-10"
        style={{
          paddingLeft: "clamp(1.25rem, 5vw, 5rem)",
          paddingRight: "clamp(1.25rem, 5vw, 5rem)",
          paddingTop: "clamp(2.5rem, 5vw, 3.5rem)",
          paddingBottom: "clamp(1rem, 2vw, 1.5rem)",
        }}
      >
        <Breadcrumbs items={crumbs} brand={brand} />
        <h1
          className="font-cormorant font-semibold text-white mt-4"
          style={{ fontSize: "clamp(1.7rem, 2.8vw, 2.8rem)", lineHeight: 1.08 }}
        >
          {headline}
        </h1>
        {subheadline && (
          <p
            className="font-outfit mt-2"
            style={{ fontSize: "clamp(0.9rem, 1vw, 1rem)", color: "rgba(220,220,220,0.85)" }}
          >
            {subheadline}
          </p>
        )}
      </div>
    </section>
  );
}


function HeroBlock({ props, tenant, site, page }) {
  const { eyebrow, headline, subheadline, cta_text, cta_href, cta2_text, cta2_href, image_url, images, rotate_ms, align, bullets, urgency_headline, urgency_body, psst_line } = props;
  const awards = site?.settings?.awards;
  const isDark = site?.settings?.theme === "dark-commercial";
  const brand = tenant?.primary_color || "#FFC107";

  // Hooks first (rules of hooks) — even if we branch to an inner header below.
  const slides = Array.isArray(images) && images.length > 0
    ? images
    : (image_url ? [{ url: image_url }] : []);
  const [slideIdx, setSlideIdx] = useState(0);
  const rotateMs = Number.isFinite(rotate_ms) && rotate_ms > 1500 ? rotate_ms : 10000;
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % slides.length), rotateMs);
    return () => clearInterval(t);
  }, [slides.length, rotateMs]);

  // Route to the correct header variant based on the current page slug so the
  // homepage full-bleed carousel never appears on interior pages.
  const kind = classifyPageKind(page?.slug);
  if (isDark && kind !== "home") {
    if (kind === "article") return <ArticleHeader props={props} tenant={tenant} page={page} />;
    if (kind === "tool") return <ToolHeader props={props} tenant={tenant} page={page} />;
    if (kind === "legal" || kind === "contact") return <LegalHeader props={props} tenant={tenant} page={page} />;
    return <InnerPageHeader props={props} tenant={tenant} page={page} />;
  }

  // Premium marble + split-panel hero for dark-commercial tenants (matches
  // the "PRECISION. SAFETY. PROFESSIONALISM." mockup). Left = charcoal marble
  // with metallic silver+gold display type. Right = diagonal-clipped photo of
  // the current carousel slide, framed with a gold hairline.
  if (isDark && slides.length > 0) {
    return (
      <section
        className="relative overflow-hidden tenant-theme-dark marble-surface-strong"
        data-block="hero"
        data-variant="home"
        data-testid="hero-carousel"
        style={{
          width: "100vw",
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          minHeight: "min(88vh, 900px)",
          backgroundImage:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.75) 100%), url(/marble-nav-bg.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Subtle top-center lighting like the mockup */}
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: -80,
            left: "50%",
            transform: "translateX(-50%)",
            width: "70vw",
            height: 320,
            background: "radial-gradient(ellipse at center, rgba(255,240,190,0.18) 0%, rgba(255,220,140,0.06) 40%, transparent 75%)",
            filter: "blur(20px)",
            zIndex: 1,
          }}
        />

        <div className="relative z-10 grid lg:grid-cols-[minmax(0,7fr)_minmax(0,6fr)] gap-0 items-stretch">
          {/* LEFT — marble + metallic display */}
          <div
            className="relative flex flex-col justify-start items-center text-center"
            style={{
              paddingLeft: "clamp(1.5rem, 6vw, 7rem)",
              paddingRight: "clamp(1.5rem, 4vw, 4rem)",
              paddingTop: "clamp(1rem, 1.6vw, 1.5rem)",
              paddingBottom: "clamp(3rem, 6vw, 5rem)",
              minHeight: "min(88vh, 900px)",
            }}
          >
            <div className="fade-up flex flex-col items-center text-center" style={{ maxWidth: 900 }}>
              {/* Centered hero logo — brand-forward, especially on mobile */}
              {tenant?.logo_url && (
                <div style={{ position: "relative", marginBottom: "clamp(0.75rem, 1.6vw, 1.4rem)" }}>
                  <span
                    aria-hidden
                    className="absolute inset-0 -z-10 rounded-full blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(229,199,122,0.5) 0%, rgba(201,162,75,0.2) 45%, transparent 70%)", transform: "scale(1.7)" }}
                  />
                  <img
                    src={tenant.logo_url}
                    alt={tenant.name}
                    data-testid="hero-center-logo"
                    style={{
                      display: "block",
                      height: "clamp(200px, 28vw, 400px)",
                      width: "auto",
                      filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.75))",
                    }}
                  />
                </div>
              )}

              {eyebrow && (
                <div
                  className="uppercase mb-6 flex items-center justify-center gap-3"
                  style={{ color: "#E5C77A", fontSize: "0.72rem", letterSpacing: "0.32em", fontWeight: 700 }}
                >
                  <span aria-hidden style={{ display: "inline-block", width: 40, height: 1, background: "linear-gradient(90deg, transparent, #E5C77A, transparent)" }} />
                  {eyebrow}
                  <span aria-hidden style={{ display: "inline-block", width: 40, height: 1, background: "linear-gradient(270deg, transparent, #E5C77A, transparent)" }} />
                </div>
              )}

              {/* Signature triple stack — silver / gold / silver.
                  Text is data-driven via props.hero_words (3-element array).
                  Falls back to the "PRECISION. / SAFETY. / PROFESSIONALISM."
                  default when no custom words are set. */}
              {(() => {
                const words = Array.isArray(props?.hero_words) && props.hero_words.length === 3
                  ? props.hero_words
                  : ["Precision.", "Safety.", "Professionalism."];
                return (
                  <h1
                    className="font-cab font-black"
                    style={{
                      fontSize: "clamp(2.6rem, 5.4vw, 5.4rem)",
                      lineHeight: 0.98,
                      letterSpacing: "-0.005em",
                      textTransform: "uppercase",
                    }}
                    data-testid="hero-triple-headline"
                  >
                    <span className="block metallic-silver">{words[0]}</span>
                    <span className="block metallic-gold">{words[1]}</span>
                    <span className="block metallic-silver">{words[2]}</span>
                  </h1>
                );
              })()}

              {/* Green cursive whisper — sits directly under the triple stack. */}
              {psst_line && (
                <p
                  style={{
                    fontFamily: "'Dancing Script', 'Caveat', cursive",
                    fontWeight: 700,
                    fontStyle: "italic",
                    color: "#5BC46B",
                    fontSize: "clamp(1.7rem, 2.6vw, 2.4rem)",
                    lineHeight: 1.05,
                    letterSpacing: "0.005em",
                    transform: "rotate(-3.5deg)",
                    transformOrigin: "center center",
                    textShadow: "0 2px 12px rgba(91,196,107,0.35)",
                    display: "inline-block",
                    marginTop: "clamp(0.9rem, 1.4vw, 1.4rem)",
                    marginBottom: "clamp(1.5rem, 2.4vw, 2.2rem)",
                  }}
                  data-testid="hero-psst-line"
                >
                  {psst_line}
                </p>
              )}

              <p
                className="uppercase font-inter"
                style={{
                  color: "#E5C77A",
                  fontSize: "clamp(0.95rem, 1.1vw, 1.1rem)",
                  letterSpacing: "0.24em",
                  fontWeight: 700,
                  maxWidth: 560,
                }}
              >
                {headline || "Premier Commercial Parking Lot Striping"}
              </p>
              {subheadline && (
                <p
                  className="mt-4 max-w-xl mx-auto"
                  style={{
                    color: "rgba(230,230,230,0.85)",
                    fontSize: "clamp(0.95rem, 1.05vw, 1.05rem)",
                    lineHeight: 1.55,
                  }}
                >
                  {subheadline}
                </p>
              )}

              {/* Checklist bullets — sales-CTA proof points */}
              {Array.isArray(bullets) && bullets.length > 0 && (
                <ul
                  className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-2xl mx-auto text-left"
                  data-testid="hero-bullets"
                >
                  {bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 font-inter"
                      style={{
                        color: "rgba(240,240,236,0.94)",
                        fontSize: "clamp(0.9rem, 0.98vw, 1rem)",
                        lineHeight: 1.35,
                      }}
                    >
                      <span
                        aria-hidden
                        className="flex-shrink-0 flex items-center justify-center rounded-full"
                        style={{
                          width: 20,
                          height: 20,
                          marginTop: 2,
                          background: "linear-gradient(145deg, #E5C77A 0%, #C9A24B 100%)",
                          color: "#1a1a1a",
                          fontSize: 11,
                          fontWeight: 900,
                          boxShadow: "0 0 8px -2px rgba(229,199,122,0.55)",
                        }}
                      >
                        ✓
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Three signature pillars — matches the mockup */}
              <div
                className="mt-10 grid grid-cols-3 gap-4 md:gap-6 max-w-lg mx-auto"
                style={{
                  borderTop: "1px solid rgba(229,199,122,0.18)",
                  paddingTop: "1.5rem",
                }}
                data-testid="hero-pillars"
              >
                {[
                  { label: "Safety First", icon: "shield" },
                  { label: "Precision Striping", icon: "target" },
                  { label: "On Time, Every Time", icon: "clock" },
                ].map((p, i) => (
                  <div key={i} className="flex flex-col items-center text-center gap-2">
                    <span
                      className="w-11 h-11 flex items-center justify-center rounded-full"
                      style={{
                        border: "1px solid #C9A24B",
                        color: "#E5C77A",
                        background: "rgba(0,0,0,0.35)",
                        boxShadow: "0 0 22px -6px rgba(229,199,122,0.45), inset 0 0 12px -6px rgba(229,199,122,0.3)",
                      }}
                      aria-hidden
                    >
                      {p.icon === "shield" ? <ShieldCheck size={20} /> : p.icon === "target" ? <Crosshair size={20} /> : <Clock size={20} />}
                    </span>
                    <span
                      className="uppercase font-inter"
                      style={{ color: "#F1F1EE", fontSize: "0.7rem", letterSpacing: "0.16em", fontWeight: 600, lineHeight: 1.2 }}
                    >
                      {p.label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap justify-center gap-3 mt-8" data-testid="hero-ctas">
                {(urgency_headline || urgency_body) && (
                  <div
                    className="w-full mb-3 pb-4 border-b text-center"
                    style={{ borderColor: "rgba(229,199,122,0.22)" }}
                    data-testid="hero-urgency"
                  >
                    {urgency_headline && (
                      <div
                        className="font-cab uppercase"
                        style={{
                          color: "#F1F1EE",
                          fontSize: "clamp(1.15rem, 1.6vw, 1.55rem)",
                          fontWeight: 800,
                          letterSpacing: "0.02em",
                          lineHeight: 1.15,
                        }}
                      >
                        {urgency_headline}
                      </div>
                    )}
                    {urgency_body && (
                      <div
                        className="font-inter mt-2 max-w-xl mx-auto"
                        style={{
                          color: "rgba(220,220,214,0.82)",
                          fontSize: "clamp(0.88rem, 0.98vw, 1rem)",
                          lineHeight: 1.5,
                        }}
                      >
                        {urgency_body}
                      </div>
                    )}
                  </div>
                )}
                {cta_text && (
                  <a href={cta_href || "#contact"} className="gold-cta" data-testid="hero-cta-primary">
                    {cta_text} <span aria-hidden>★</span>
                  </a>
                )}
                {cta2_text && (
                  <a
                    href={cta2_href || "#contact"}
                    className="leen-btn"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      color: "#F1F1EE",
                      border: "1px solid rgba(229,199,122,0.35)",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    {cta2_text}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT — diagonal-clipped photo panel with gold edge */}
          <div className="relative hidden lg:block" data-testid="hero-photo-panel">
            {/* Floating chat card — home page only. Overlays the aerial photo
                so the "talk to us now" prompt sits right where the eye lands. */}
            {(page?.slug === "/" || page?.slug === "/es") && (
              <div
                style={{
                  position: "absolute",
                  right: 32,
                  bottom: 40,
                  zIndex: 8,
                  width: "min(420px, 38vw)",
                  minWidth: 340,
                }}
                data-testid="hero-chat-desktop-wrapper"
              >
                <HeroChat tenant={tenant} isSpanish={page?.slug === "/es"} />
              </div>
            )}
            <div
              className="absolute inset-0"
              style={{
                clipPath: "polygon(12% 0, 100% 0, 100% 100%, 0 100%)",
              }}
            >
              {slides.map((s, i) => (
                <img
                  key={s.url + i}
                  src={s.url}
                  alt={s.alt || ""}
                  data-testid={`hero-slide-${i}`}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    objectPosition: s.object_position || "center 55%",
                    opacity: slideIdx === i ? 1 : 0,
                    transition: "opacity 1000ms ease-in-out",
                    zIndex: slideIdx === i ? 1 : 0,
                  }}
                />
              ))}
              {/* Subtle inner shadow at the diagonal edge for depth */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 22%)",
                  zIndex: 2,
                }}
              />
            </div>
            {/* Gold hairline along the diagonal cut */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ zIndex: 3 }}
              aria-hidden
            >
              <defs>
                <linearGradient id="goldEdge" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#8F6E27" />
                  <stop offset="50%" stopColor="#E5C77A" />
                  <stop offset="100%" stopColor="#C9A24B" />
                </linearGradient>
              </defs>
              <line x1="12" y1="0" x2="0" y2="100" stroke="url(#goldEdge)" strokeWidth="0.35" vectorEffect="non-scaling-stroke" style={{ filter: "drop-shadow(0 0 4px rgba(229,199,122,0.65))" }} />
            </svg>
          </div>
        </div>

        {/* Mobile-only photo strip below the marble panel */}
        <div className="relative lg:hidden w-full" style={{ height: "38vh", minHeight: 260, borderTop: "1px solid rgba(229,199,122,0.35)" }}>
          {slides.map((s, i) => (
            <img
              key={s.url + "m" + i}
              src={s.url}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                objectPosition: s.object_position || "center 55%",
                opacity: slideIdx === i ? 1 : 0,
                transition: "opacity 900ms ease-in-out",
                zIndex: slideIdx === i ? 1 : 0,
              }}
            />
          ))}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 40%)" }}
          />
        </div>

        {/* Slide indicator dots — bottom-right, gold */}
        {slides.length > 1 && (
          <div
            className="absolute z-20 flex items-center gap-2"
            style={{ bottom: awards?.enabled && awards.awards?.length > 0 ? "6rem" : "1.75rem", right: "clamp(1rem, 3vw, 3rem)" }}
            data-testid="hero-carousel-dots"
          >
            {slides.map((s, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show slide ${i + 1}`}
                onClick={() => setSlideIdx(i)}
                data-testid={`hero-dot-${i}`}
                style={{
                  width: slideIdx === i ? 28 : 10,
                  height: 8,
                  borderRadius: 999,
                  border: `1px solid ${slideIdx === i ? "#E5C77A" : "rgba(229,199,122,0.4)"}`,
                  background: slideIdx === i ? "linear-gradient(180deg, #E5C77A, #C9A24B)" : "rgba(255,255,255,0.14)",
                  cursor: "pointer",
                  transition: "width 400ms, background 400ms",
                  padding: 0,
                }}
              />
            ))}
          </div>
        )}

        {/* Awards strip — kept, gold accents cascade from tenant primary_color */}
        {awards?.enabled && awards.awards?.length > 0 && (
          <HeroAwardsStrip awards={awards} tenant={tenant} />
        )}

        {/* Mobile-only inline chat card (below the photo strip). Home page only. */}
        {(page?.slug === "/" || page?.slug === "/es") && (
          <div className="lg:hidden px-4 sm:px-6 py-8" data-testid="hero-chat-mobile-wrapper" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.85) 100%)" }}>
            <div style={{ margin: "0 auto", maxWidth: 460 }}>
              <HeroChat tenant={tenant} isSpanish={page?.slug === "/es"} />
            </div>
          </div>
        )}
      </section>
    );
  }

  // Fallback (light theme / no image) — original layout
  return (
    <section className="relative overflow-hidden" data-block="hero">
      {image_url && (
        <>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${image_url}')` }} />
          <div className="absolute inset-0 hero-scrim" />
        </>
      )}
      {awards?.enabled && awards.awards?.length > 0 && (
        <AwardsRibbon awards={awards} tenant={tenant} />
      )}
      <div className={`relative max-w-6xl mx-auto px-6 py-24 md:py-36 grid md:grid-cols-2 gap-10 items-center ${align === "center" ? "text-center md:grid-cols-1" : ""}`}>
        <div>
          {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-6" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          <h1 className="font-cormorant text-5xl md:text-7xl font-semibold tracking-tight leading-[1.02]" style={{ color: "var(--l-text)" }}>{headline}</h1>
          {subheadline && <p className="font-outfit text-lg mt-8 max-w-md leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
          <div className="flex flex-wrap gap-3 mt-10">
            {cta_text && (
              <a href={cta_href || "#contact"} className="leen-btn" style={{ background: tenant.primary_color, color: readableTextOn(tenant.primary_color) }}>
                {cta_text}
              </a>
            )}
            {cta2_text && (
              <a href={cta2_href || "#contact"} className="leen-btn" style={{ background: "transparent", color: tenant.primary_color, border: `1px solid ${tenant.primary_color}` }}>
                {cta2_text}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
const HeroDef = {
  Component: HeroBlock, label: "Hero",
  defaultProps: { eyebrow: "", headline: "Welcome", subheadline: "", cta_text: "Get started", cta_href: "#contact", cta2_text: "", cta2_href: "", image_url: "", align: "left", bullets: [], urgency_headline: "", urgency_body: "" },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "bullets", label: "Checkmark bullets (one per line)", type: "list_text" },
    { key: "urgency_headline", label: "Urgency headline (above CTAs)", type: "text" },
    { key: "urgency_body", label: "Urgency body (above CTAs)", type: "textarea" },
    { key: "cta_text", label: "Primary CTA text", type: "text" },
    { key: "cta_href", label: "Primary CTA link", type: "text" },
    { key: "cta2_text", label: "Secondary CTA text", type: "text" },
    { key: "cta2_href", label: "Secondary CTA link", type: "text" },
    { key: "image_url", label: "Background image URL", type: "url" },
    { key: "align", label: "Alignment", type: "select", options: ["left", "center"] },
  ],
};

// ============================================================
// SERVICES GRID
// ============================================================
function ServicesGridBlock({ props, tenant }) {
  const { eyebrow, headline, items = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="services_grid">
      {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="font-cormorant text-5xl font-semibold tracking-tight max-w-xl" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {items.map((it, idx) => {
          const cardBody = (
            <>
              <Icon name={it.icon} size={28} strokeWidth={1.4} style={{ color: tenant.primary_color }} />
              <div className="font-cormorant text-2xl font-semibold mt-4" style={{ color: "var(--l-text)" }}>{it.title}</div>
              <div className="font-outfit text-[15px] mt-3 leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{it.body}</div>
              {it.href && (
                <div
                  className="font-outfit text-xs uppercase tracking-[0.22em] mt-6 inline-flex items-center gap-2 transition-transform group-hover:translate-x-1"
                  style={{ color: tenant.primary_color, fontWeight: 700 }}
                >
                  Open <span aria-hidden>→</span>
                </div>
              )}
            </>
          );
          const cardClass =
            "group block border border-[var(--l-border)] rounded-md p-8 bg-[var(--l-surface)] transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-[color:var(--l-accent-gold)]";
          return it.href ? (
            <a
              key={idx}
              href={it.href}
              className={cardClass}
              data-testid={`services-grid-card-${idx}`}
            >
              {cardBody}
            </a>
          ) : (
            <div key={idx} className={cardClass} data-testid={`services-grid-card-${idx}`}>
              {cardBody}
            </div>
          );
        })}
      </div>
    </section>
  );
}
const ServicesGridDef = {
  Component: ServicesGridBlock, label: "Services grid",
  defaultProps: { eyebrow: "What we do", headline: "Services", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "items", label: "Services", type: "items", itemSchema: [
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Description", type: "textarea" },
      { key: "icon", label: "Icon", type: "select", options: Object.keys(ICONS) },
    ]},
  ],
};

// ============================================================
// WHY CHOOSE US
// ============================================================
function WhyChooseUsBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, items = [], image_url } = props;
  return (
    <section className="bg-[var(--l-surface)] border-y border-[var(--l-border)]" data-block="why_choose_us">
      <div className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          {headline && <h2 className="font-cormorant text-4xl md:text-5xl font-semibold leading-tight" style={{ color: "var(--l-text)" }}>{headline}</h2>}
          {subheadline && <p className="font-outfit mt-4 text-lg" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
          <ul className="space-y-4 mt-8">
            {items.map((it, idx) => (
              <li key={idx} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${tenant.primary_color}18` }}>
                  <Icon name={it.icon || "Check"} size={18} strokeWidth={2} style={{ color: tenant.primary_color }} />
                </div>
                <div>
                  <div className="font-cormorant text-xl font-semibold" style={{ color: "var(--l-text)" }}>{it.title}</div>
                  <div className="font-outfit text-[15px] mt-1 leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{it.body}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        {image_url && <img src={image_url} alt="" className="rounded-md w-full h-[440px] object-cover" />}
      </div>
    </section>
  );
}
const WhyChooseUsDef = {
  Component: WhyChooseUsBlock, label: "Why choose us",
  defaultProps: { eyebrow: "Why us", headline: "Why choose us", subheadline: "", image_url: "", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "image_url", label: "Image URL", type: "url" },
    { key: "items", label: "Reasons", type: "items", itemSchema: [
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Body", type: "textarea" },
      { key: "icon", label: "Icon", type: "select", options: Object.keys(ICONS) },
    ]},
  ],
};

// ============================================================
// PROCESS / HOW IT WORKS
// ============================================================
function ProcessBlock({ props, tenant }) {
  const { eyebrow, headline, items = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="process">
      {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="font-cormorant text-4xl md:text-5xl font-semibold tracking-tight mb-12" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      <div className="grid md:grid-cols-4 gap-6 relative">
        {items.map((it, idx) => (
          <div key={idx} className="relative">
            <div className="w-14 h-14 rounded-full flex items-center justify-center font-cormorant text-2xl font-semibold text-white mb-4" style={{ background: tenant.primary_color }}>
              {idx + 1}
            </div>
            <div className="font-cormorant text-2xl font-semibold" style={{ color: "var(--l-text)" }}>{it.title}</div>
            <div className="font-outfit text-[15px] mt-2 leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{it.body}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
const ProcessDef = {
  Component: ProcessBlock, label: "Process / How it works",
  defaultProps: { eyebrow: "How it works", headline: "Our process", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "items", label: "Steps", type: "items", itemSchema: [
      { key: "title", label: "Step title", type: "text" },
      { key: "body", label: "Description", type: "textarea" },
    ]},
  ],
};

// ============================================================
// PRICING TABLE
// ============================================================
function PricingBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, plans = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="pricing">
      <div className="text-center max-w-2xl mx-auto mb-14">
        {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
        {headline && <h2 className="font-cormorant text-4xl md:text-5xl font-semibold" style={{ color: "var(--l-text)" }}>{headline}</h2>}
        {subheadline && <p className="font-outfit mt-4" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((p, idx) => {
          const featured = p.featured === true || p.featured === "true";
          return (
            <div
              key={idx}
              className="border rounded-md p-8 flex flex-col relative"
              style={{
                borderColor: featured ? tenant.primary_color : "var(--l-border)",
                background: featured ? tenant.primary_color : "var(--l-surface)",
                color: featured ? "white" : "inherit",
              }}
            >
              {featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-xs uppercase tracking-widest px-3 py-1 rounded-full border border-[var(--l-border)]" style={{ color: tenant.primary_color }}>Most popular</div>}
              <div className="font-cormorant text-2xl font-semibold" style={{ color: featured ? "white" : "var(--l-text)" }}>{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-cormorant text-5xl font-semibold" style={{ color: featured ? "white" : "var(--l-text)" }}>{p.price}</span>
                {p.period && <span className="font-outfit text-sm opacity-80">/{p.period}</span>}
              </div>
              {p.body && <p className="font-outfit text-sm mt-3" style={{ color: featured ? "rgba(255,255,255,0.9)" : "var(--l-text-sec)" }}>{p.body}</p>}
              <ul className="space-y-2 mt-6 font-outfit text-sm flex-1">
                {(p.features || "").split("\n").filter(Boolean).map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={16} strokeWidth={2} style={{ color: featured ? "white" : tenant.primary_color, flexShrink: 0, marginTop: 2 }} />
                    <span style={{ color: featured ? "rgba(255,255,255,0.95)" : "var(--l-text)" }}>{f}</span>
                  </li>
                ))}
              </ul>
              <a href={p.cta_href || "#contact"} className="mt-8 block text-center py-3 rounded-md font-outfit font-medium transition-colors" style={{ background: featured ? "white" : tenant.primary_color, color: featured ? tenant.primary_color : "white" }}>
                {p.cta_text || "Get started"}
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}
const PricingDef = {
  Component: PricingBlock, label: "Pricing table",
  defaultProps: { eyebrow: "Pricing", headline: "Simple, transparent pricing", subheadline: "", plans: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "plans", label: "Plans", type: "items", itemSchema: [
      { key: "name", label: "Plan name", type: "text" },
      { key: "price", label: "Price (e.g. $99)", type: "text" },
      { key: "period", label: "Period (e.g. month)", type: "text" },
      { key: "body", label: "One-liner", type: "textarea" },
      { key: "features", label: "Features (one per line)", type: "textarea" },
      { key: "cta_text", label: "CTA text", type: "text" },
      { key: "cta_href", label: "CTA link", type: "text" },
      { key: "featured", label: "Featured (true/false)", type: "text" },
    ]},
  ],
};

// ============================================================
// TEAM
// ============================================================
function TeamBlock({ props, tenant }) {
  const { eyebrow, headline, items = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="team">
      {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="font-cormorant text-4xl md:text-5xl font-semibold mb-12" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8">
        {items.map((it, idx) => (
          <div key={idx} className="text-center">
            {it.photo_url ? (
              <img src={it.photo_url} alt={it.name} className="w-32 h-32 rounded-full object-cover mx-auto mb-4" />
            ) : (
              <div className="w-32 h-32 rounded-full mx-auto mb-4 flex items-center justify-center font-cormorant text-3xl font-semibold text-white" style={{ background: tenant.primary_color }}>
                {(it.name || "?")[0]}
              </div>
            )}
            <div className="font-cormorant text-2xl font-semibold" style={{ color: "var(--l-text)" }}>{it.name}</div>
            <div className="font-outfit text-xs uppercase tracking-wider mt-1" style={{ color: tenant.primary_color }}>{it.role}</div>
            {it.bio && <div className="font-outfit text-sm mt-3 leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{it.bio}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}
const TeamDef = {
  Component: TeamBlock, label: "Team",
  defaultProps: { eyebrow: "The team", headline: "Meet the team", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "items", label: "Members", type: "items", itemSchema: [
      { key: "name", label: "Name", type: "text" },
      { key: "role", label: "Role", type: "text" },
      { key: "bio", label: "Bio", type: "textarea" },
      { key: "photo_url", label: "Photo URL", type: "url" },
    ]},
  ],
};

// ============================================================
// LOGOS STRIP
// ============================================================
function LogosStripBlock({ props, tenant }) {
  const { eyebrow, items = [] } = props;
  return (
    <section className="border-y border-[var(--l-border)] bg-[var(--l-surface)] py-12" data-block="logos_strip">
      <div className="max-w-6xl mx-auto px-6">
        {eyebrow && <div className="text-center font-outfit text-xs uppercase tracking-[0.3em] mb-8" style={{ color: "var(--l-text-sec)" }}>{eyebrow}</div>}
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
          {items.map((it, idx) => (
            it.url ? (
              <img
                key={idx}
                src={it.url}
                alt={it.name || ""}
                className="h-10 opacity-60 hover:opacity-100 grayscale hover:grayscale-0 transition-all"
              />
            ) : (
              <div key={idx} className="font-cormorant text-2xl font-semibold opacity-40 hover:opacity-100 transition-opacity" style={{ color: "var(--l-text)" }}>
                {it.name}
              </div>
            )
          ))}
        </div>
      </div>
    </section>
  );
}
const LogosStripDef = {
  Component: LogosStripBlock, label: "Client logos strip",
  defaultProps: { eyebrow: "Trusted by", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "items", label: "Logos", type: "items", itemSchema: [
      { key: "name", label: "Name (fallback text)", type: "text" },
      { key: "url", label: "Logo image URL", type: "url" },
    ]},
  ],
};

// ============================================================
// FINANCING BANNER
// ============================================================
function FinancingBannerBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, cta_text, cta_href } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-12" data-block="financing_banner">
      <div className="rounded-md border-2 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" style={{ borderColor: tenant.primary_color, background: `${tenant.primary_color}0a` }}>
        <div className="flex items-center gap-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: tenant.primary_color }}>
            <DollarSign size={26} color="white" strokeWidth={2} />
          </div>
          <div>
            {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.2em]" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
            <div className="font-cormorant text-3xl font-semibold" style={{ color: "var(--l-text)" }}>{headline}</div>
            {subheadline && <div className="font-outfit text-sm mt-1" style={{ color: "var(--l-text-sec)" }}>{subheadline}</div>}
          </div>
        </div>
        {cta_text && (
          <a href={cta_href || "#contact"} className="leen-btn whitespace-nowrap" style={{ background: tenant.primary_color }}>
            {cta_text}
          </a>
        )}
      </div>
    </section>
  );
}
const FinancingBannerDef = {
  Component: FinancingBannerBlock, label: "Financing banner",
  defaultProps: { eyebrow: "Financing available", headline: "0% financing for 12 months", subheadline: "Ask about qualified financing options for your project.", cta_text: "Apply now", cta_href: "#contact" },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "cta_text", label: "CTA text", type: "text" },
    { key: "cta_href", label: "CTA link", type: "text" },
  ],
};

// ============================================================
// VIDEO / EMBED
// ============================================================
function toEmbedUrl(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return url;
}
function VideoBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, video_url } = props;
  const src = toEmbedUrl(video_url);
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="video">
      {(eyebrow || headline) && (
        <div className="text-center max-w-2xl mx-auto mb-10">
          {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          {headline && <h2 className="font-cormorant text-4xl md:text-5xl font-semibold" style={{ color: "var(--l-text)" }}>{headline}</h2>}
          {subheadline && <p className="font-outfit mt-3" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
        </div>
      )}
      {src ? (
        <div className="relative w-full rounded-md overflow-hidden border border-[var(--l-border)]" style={{ paddingBottom: "56.25%" }}>
          <iframe title="Video" src={src} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      ) : (
        <div className="p-10 border border-dashed border-[var(--l-border)] text-center font-outfit text-sm rounded-md" style={{ color: "var(--l-text-sec)" }}>
          Add a YouTube or Vimeo URL in the editor.
        </div>
      )}
    </section>
  );
}
const VideoDef = {
  Component: VideoBlock, label: "Video / embed",
  defaultProps: { eyebrow: "", headline: "", subheadline: "", video_url: "" },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "video_url", label: "YouTube / Vimeo URL", type: "url" },
  ],
};

// ============================================================
// INTERACTIVE MAP
// ============================================================
function MapBlock({ props, tenant }) {
  const { eyebrow, headline, address, height } = props;
  const src = address
    ? `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`
    : "";
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="map">
      {(eyebrow || headline) && (
        <div className="mb-8">
          {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          {headline && <h2 className="font-cormorant text-4xl font-semibold" style={{ color: "var(--l-text)" }}>{headline}</h2>}
          {address && <div className="font-outfit mt-3 flex items-center gap-2" style={{ color: "var(--l-text-sec)" }}><MapPin size={14} /> {address}</div>}
        </div>
      )}
      {src ? (
        <iframe title="Location map" src={src} className="w-full rounded-md border border-[var(--l-border)]" style={{ height: height || 420 }} loading="lazy" />
      ) : (
        <div className="p-10 border border-dashed border-[var(--l-border)] text-center font-outfit text-sm rounded-md" style={{ color: "var(--l-text-sec)" }}>
          Add an address to render an interactive map.
        </div>
      )}
    </section>
  );
}
const MapDef = {
  Component: MapBlock, label: "Interactive map",
  defaultProps: { eyebrow: "Visit us", headline: "Find us", address: "", height: 420 },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "address", label: "Address (or search query)", type: "text" },
    { key: "height", label: "Map height (px)", type: "text" },
  ],
};

// ============================================================
// TRUST BADGES
// ============================================================
function TrustBadgesBlock({ props, tenant }) {
  const { items = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-16" data-block="trust_badges">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((it, idx) => (
          <div key={idx} className="border border-[var(--l-border)] rounded-md p-6 text-center bg-[var(--l-surface)]">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: `${tenant.primary_color}18` }}>
              <Icon name={it.icon || "BadgeCheck"} size={22} strokeWidth={1.6} style={{ color: tenant.primary_color }} />
            </div>
            <div className="font-cormorant text-xl font-semibold" style={{ color: "var(--l-text)" }}>{it.title}</div>
            {it.body && <div className="font-outfit text-xs mt-1" style={{ color: "var(--l-text-sec)" }}>{it.body}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}
const TrustBadgesDef = {
  Component: TrustBadgesBlock, label: "Trust badges",
  defaultProps: { items: [] },
  schema: [
    { key: "items", label: "Badges", type: "items", itemSchema: [
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Sub-text", type: "text" },
      { key: "icon", label: "Icon", type: "select", options: Object.keys(ICONS) },
    ]},
  ],
};

// ============================================================
// BEFORE & AFTER GALLERY
// ============================================================
function BeforeAfterBlock({ props, tenant }) {
  const { eyebrow, headline, items = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="before_after">
      {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="font-cormorant text-4xl md:text-5xl font-semibold mb-12" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      <div className="space-y-16">
        {items.map((it, idx) => (
          <div key={idx}>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="relative">
                <div className="absolute top-3 left-3 z-10 px-3 py-1 text-xs font-outfit uppercase tracking-widest rounded-full text-white" style={{ background: "rgba(0,0,0,0.7)" }}>Before</div>
                {it.before_url && <img src={it.before_url} alt="Before" className="w-full h-72 md:h-96 object-cover rounded-md" />}
              </div>
              <div className="relative">
                <div className="absolute top-3 left-3 z-10 px-3 py-1 text-xs font-outfit uppercase tracking-widest rounded-full text-white" style={{ background: tenant.primary_color }}>After</div>
                {it.after_url && <img src={it.after_url} alt="After" className="w-full h-72 md:h-96 object-cover rounded-md" />}
              </div>
            </div>
            {(it.title || it.body) && (
              <div className="mt-4">
                {it.title && <div className="font-cormorant text-2xl font-semibold" style={{ color: "var(--l-text)" }}>{it.title}</div>}
                {it.body && <div className="font-outfit mt-1" style={{ color: "var(--l-text-sec)" }}>{it.body}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
const BeforeAfterDef = {
  Component: BeforeAfterBlock, label: "Before & after gallery",
  defaultProps: { eyebrow: "Our work", headline: "Before & after", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "items", label: "Projects", type: "items", itemSchema: [
      { key: "before_url", label: "Before image URL", type: "url" },
      { key: "after_url", label: "After image URL", type: "url" },
      { key: "title", label: "Project title", type: "text" },
      { key: "body", label: "Description", type: "textarea" },
    ]},
  ],
};

// ============================================================
// TESTIMONIALS CAROUSEL
// ============================================================
function TestimonialsCarouselBlock({ props, tenant }) {
  const { eyebrow, headline, items = [], auto_advance_ms } = props;
  const [idx, setIdx] = useState(0);
  const count = items.length;
  const delay = parseInt(auto_advance_ms, 10) || 6000;
  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), delay);
    return () => clearInterval(t);
  }, [count, delay]);
  if (count === 0) return null;
  const current = items[idx % count];
  return (
    <section className="max-w-4xl mx-auto px-6 py-24" data-block="testimonials_carousel">
      {eyebrow && <div className="text-center font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="text-center font-cormorant text-4xl md:text-5xl font-semibold mb-10" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      <div className="border border-[var(--l-border)] rounded-md p-10 md:p-14 bg-[var(--l-surface)] text-center relative">
        <div className="flex justify-center gap-1 mb-6">
          {Array.from({ length: current.stars || 5 }).map((_, i) => <Star key={i} size={18} fill={tenant.primary_color} strokeWidth={0} />)}
        </div>
        <p className="font-cormorant text-2xl md:text-3xl italic leading-relaxed" style={{ color: "var(--l-text)" }}>
          &ldquo;{current.body}&rdquo;
        </p>
        <div className="font-outfit text-sm mt-6" style={{ color: "var(--l-text-sec)" }}>— {current.author}{current.location ? `, ${current.location}` : ""}</div>
        {count > 1 && (
          <>
            <button onClick={() => setIdx((i) => (i - 1 + count) % count)} className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center border border-[var(--l-border)] hover:bg-white transition-colors" data-testid="testimonials-prev">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setIdx((i) => (i + 1) % count)} className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center border border-[var(--l-border)] hover:bg-white transition-colors" data-testid="testimonials-next">
              <ChevronRight size={16} />
            </button>
            <div className="flex justify-center gap-2 mt-8">
              {items.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} className="w-2 h-2 rounded-full transition-colors" style={{ background: i === idx ? tenant.primary_color : "var(--l-border)" }} aria-label={`Slide ${i + 1}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
const TestimonialsCarouselDef = {
  Component: TestimonialsCarouselBlock, label: "Testimonials carousel",
  defaultProps: { eyebrow: "What clients say", headline: "Real reviews", items: [], auto_advance_ms: 6000 },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "auto_advance_ms", label: "Auto-advance (ms)", type: "text" },
    { key: "items", label: "Testimonials", type: "items", itemSchema: [
      { key: "body", label: "Quote", type: "textarea" },
      { key: "author", label: "Author", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "stars", label: "Stars (1-5)", type: "text" },
    ]},
  ],
};

// ============================================================
// CTA SPLIT (variation)
// ============================================================
function CtaSplitBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, cta_text, cta_href, cta2_text, cta2_href, style } = props;
  const dark = style === "dark";
  return (
    <section className="py-20" data-block="cta_split" style={{ background: dark ? tenant.secondary_color : `${tenant.primary_color}0d` }}>
      <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
        <div>
          {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          <h3 className="font-cormorant text-4xl md:text-5xl font-semibold leading-tight" style={{ color: dark ? "white" : "var(--l-text)" }}>{headline}</h3>
          {subheadline && <p className="font-outfit mt-4 leading-relaxed" style={{ color: dark ? "rgba(255,255,255,0.8)" : "var(--l-text-sec)" }}>{subheadline}</p>}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
          {cta_text && (
            <a href={cta_href || "#contact"} className="leen-btn text-center" style={{ background: tenant.primary_color, color: readableTextOn(tenant.primary_color) }}>{cta_text}</a>
          )}
          {cta2_text && (
            <a href={cta2_href || "#contact"} className="leen-btn text-center" style={{ background: dark ? "white" : "transparent", color: dark ? tenant.primary_color : tenant.primary_color, border: `1px solid ${dark ? "white" : tenant.primary_color}` }}>{cta2_text}</a>
          )}
        </div>
      </div>
    </section>
  );
}
const CtaSplitDef = {
  Component: CtaSplitBlock, label: "CTA (split)",
  defaultProps: { eyebrow: "", headline: "Ready to get started?", subheadline: "", cta_text: "Get a free quote", cta_href: "#contact", cta2_text: "Call us", cta2_href: "tel:", style: "light" },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "cta_text", label: "Primary CTA", type: "text" },
    { key: "cta_href", label: "Primary link", type: "text" },
    { key: "cta2_text", label: "Secondary CTA", type: "text" },
    { key: "cta2_href", label: "Secondary link", type: "text" },
    { key: "style", label: "Style", type: "select", options: ["light", "dark"] },
  ],
};

// ============================================================
// ABOUT SECTION
// ============================================================
function AboutSectionBlock({ props, tenant }) {
  const { eyebrow, headline, body, image_url } = props;
  return (
    <section className="bg-[var(--l-surface)] border-y border-[var(--l-border)]" data-block="about_section">
      <div className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
        {image_url && <img src={image_url} alt="" className="rounded-md w-full h-[380px] object-cover" />}
        <div>
          {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          <h3 className="font-cormorant text-4xl font-semibold leading-tight" style={{ color: "var(--l-text)" }}>{headline}</h3>
          <p className="font-outfit mt-6 leading-relaxed whitespace-pre-wrap" style={{ color: "var(--l-text-sec)" }}>{body}</p>
        </div>
      </div>
    </section>
  );
}
const AboutSectionDef = {
  Component: AboutSectionBlock, label: "About section",
  defaultProps: { eyebrow: "About", headline: "Our story", body: "", image_url: "" },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "body", label: "Body", type: "textarea" },
    { key: "image_url", label: "Image URL", type: "url" },
  ],
};

// ============================================================
// FAQ
// ============================================================
function FaqBlock({ props, tenant }) {
  const { eyebrow, headline, items = [] } = props;
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-3xl mx-auto px-6 py-24" data-block="faq">
      {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="font-cormorant text-4xl font-semibold mb-8" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      <div className="divide-y" style={{ borderColor: "var(--l-border)" }}>
        {items.map((it, idx) => {
          const isOpen = open === idx;
          return (
            <div key={idx} className="py-4">
              <button onClick={() => setOpen(isOpen ? -1 : idx)} className="w-full text-left flex items-center justify-between gap-4">
                <span className="font-outfit text-lg font-medium" style={{ color: "var(--l-text)" }}>{it.q || it.question}</span>
                <ChevronDown size={18} className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} style={{ color: tenant.primary_color }} />
              </button>
              {isOpen && <div className="mt-3 font-outfit leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{it.a || it.answer}</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
const FaqDef = {
  Component: FaqBlock, label: "FAQ",
  defaultProps: { eyebrow: "FAQ", headline: "Frequently asked questions", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "items", label: "Questions", type: "items", itemSchema: [
      { key: "q", label: "Question", type: "text" },
      { key: "a", label: "Answer", type: "textarea" },
    ]},
  ],
};

// ============================================================
// CONTACT FORM
// ============================================================
function ContactFormBlock({ props, tenant }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const session_id = localStorage.getItem(`nariai_chat_session_${tenant.slug}`) || undefined;
      await axios.post(`${API}/public/leads`, { tenant_slug: tenant.slug, ...form, session_id });
      setSent(true);
      // GA4 conversion tracking — no PII, only categorical form context.
      try {
        // Lazy-import so this file has zero direct analytics dependency.
        // eslint-disable-next-line
        const A = require("@/lib/analytics");
        const form_name = (props?.form_kind === "quote") ? "request_a_quote" : "contact_form";
        A.trackFormSubmit(form_name);
        A.trackGenerateLead(form_name);
      } catch { /* analytics never breaks form submit */ }
    } catch { alert("Couldn't submit. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <section id="contact" className="max-w-6xl mx-auto px-6 py-24" data-block="contact_form">
      <div className="grid md:grid-cols-5 gap-12">
        <div className="md:col-span-2">
          {props.eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{props.eyebrow}</div>}
          <h3 className="font-cormorant text-5xl font-semibold leading-tight" style={{ color: "var(--l-text)" }}>{props.headline}</h3>
          {props.sub && <p className="font-outfit mt-6 leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{props.sub}</p>}
        </div>
        <div className="md:col-span-3">
          {sent ? (
            <div className="border border-[var(--l-border)] bg-[var(--l-surface)] rounded-md p-10 text-center">
              <div className="font-cormorant text-3xl font-semibold" style={{ color: "var(--l-text)" }}>{props.success_message}</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input className="leen-input" placeholder="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="public-form-name" />
                <input className="leen-input" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="public-form-email" />
              </div>
              <input className="leen-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="public-form-phone" />
              <textarea rows={4} className="leen-input" placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="public-form-message" />
              <button type="submit" disabled={busy} className="leen-btn" style={{ background: tenant.primary_color, color: readableTextOn(tenant.primary_color) }} data-testid="public-form-submit">
                {busy ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
const ContactFormDef = {
  Component: ContactFormBlock, label: "Contact form",
  defaultProps: { eyebrow: "Get in touch", headline: "Send us a message", sub: "", success_message: "Thanks — we'll be in touch." },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "sub", label: "Sub-heading", type: "textarea" },
    { key: "success_message", label: "Success message", type: "text" },
  ],
};

// ============================================================
// CTA BANNER (existing full-width)
// ============================================================
function CtaBannerBlock({ props, tenant }) {
  return (
    <section className="py-16" data-block="cta_banner" style={{ background: tenant.primary_color }}>
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h3 className="font-cormorant text-4xl md:text-5xl font-semibold text-white">{props.headline}</h3>
        {props.cta_text && (
          <a href={props.cta_href || "/contact"} className="inline-block mt-8 bg-white/95 hover:bg-white font-outfit font-medium px-8 py-3 rounded-md transition-colors" style={{ color: "#0D0D0D" }}>
            {props.cta_text}
          </a>
        )}
      </div>
    </section>
  );
}
const CtaBannerDef = {
  Component: CtaBannerBlock, label: "CTA banner",
  defaultProps: { headline: "Ready to get started?", cta_text: "Contact us", cta_href: "/contact" },
  schema: [
    { key: "headline", label: "Headline", type: "text" },
    { key: "cta_text", label: "CTA text", type: "text" },
    { key: "cta_href", label: "CTA link", type: "text" },
  ],
};

// ============================================================
// CALL NOW BANNER
// ============================================================
function CallNowBlock({ props, tenant }) {
  return (
    <section className="max-w-4xl mx-auto px-6 py-12" data-block="call_now_banner">
      <div className="rounded-md border-2 p-6 flex items-center justify-between flex-wrap gap-4" style={{ borderColor: tenant.primary_color }}>
        <div>
          <div className="font-outfit text-xs uppercase tracking-[0.2em]" style={{ color: tenant.primary_color }}>Call us</div>
          <div className="font-cormorant text-3xl font-semibold" style={{ color: "var(--l-text)" }}>{props.headline}</div>
        </div>
        <a href={`tel:${(props.phone || "").replace(/[^0-9+]/g, "")}`} className="leen-btn flex items-center gap-2" style={{ background: tenant.primary_color, color: readableTextOn(tenant.primary_color) }}>
          <Phone size={16} /> {props.phone}
        </a>
      </div>
    </section>
  );
}
const CallNowDef = {
  Component: CallNowBlock, label: "Call now banner",
  defaultProps: { headline: "Prefer to talk?", phone: "" },
  schema: [
    { key: "headline", label: "Headline", type: "text" },
    { key: "phone", label: "Phone", type: "text" },
  ],
};

// ============================================================
// RICH TEXT
// ============================================================
function RichTextBlock({ props, tenant }) {
  const { eyebrow, headline, body } = props;
  return (
    <section className="max-w-3xl mx-auto px-6 py-16 font-outfit prose prose-lg" data-block="rich_text" style={{ color: "var(--l-text-sec)" }}>
      {eyebrow && (
        <div
          className="font-outfit text-xs uppercase tracking-[0.3em] mb-3 not-prose"
          style={{ color: tenant?.primary_color || "#FFC107" }}
        >
          {eyebrow}
        </div>
      )}
      {headline && (
        <h2
          className="font-cormorant text-4xl md:text-5xl font-semibold mb-6 not-prose"
          style={{ color: "var(--l-text)", lineHeight: 1.1 }}
        >
          {headline}
        </h2>
      )}
      {body && <div dangerouslySetInnerHTML={{ __html: body }} />}
    </section>
  );
}
const RichTextDef = {
  Component: RichTextBlock, label: "Rich text",
  defaultProps: { eyebrow: "", headline: "", body: "<p>Write something here…</p>" },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "body", label: "HTML content", type: "textarea", rows: 12 },
  ],
};

// ============================================================
// IMAGE + TEXT
// ============================================================
function ImageTextBlock({ props, tenant }) {
  const { image_url, image_side = "left", headline, body, eyebrow } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="image_text">
      <div className={`grid md:grid-cols-2 gap-12 items-center ${image_side === "right" ? "md:[&>*:first-child]:order-2" : ""}`}>
        {image_url && <img src={image_url} alt="" className="rounded-md w-full h-[400px] object-cover" />}
        <div>
          {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          {headline && <h3 className="font-cormorant text-4xl font-semibold" style={{ color: "var(--l-text)" }}>{headline}</h3>}
          {body && <p className="font-outfit mt-6 leading-relaxed whitespace-pre-wrap" style={{ color: "var(--l-text-sec)" }}>{body}</p>}
        </div>
      </div>
    </section>
  );
}
const ImageTextDef = {
  Component: ImageTextBlock, label: "Image + text",
  defaultProps: { image_url: "", image_side: "left", eyebrow: "", headline: "", body: "" },
  schema: [
    { key: "image_url", label: "Image URL", type: "url" },
    { key: "image_side", label: "Image side", type: "select", options: ["left", "right"] },
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "body", label: "Body", type: "textarea" },
  ],
};

// ============================================================
// REVIEWS (static grid — 3-column)
// ============================================================
function ReviewsBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, items = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 md:py-24" data-block="reviews">
      {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3 text-center" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="font-cormorant text-4xl md:text-5xl font-semibold mb-4 text-center" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      {subheadline && <p className="font-outfit text-base md:text-lg leading-relaxed mb-14 text-center max-w-3xl mx-auto" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {items.map((it, idx) => (
          <blockquote key={idx} className="border border-[var(--l-border)] p-7 rounded-md bg-[var(--l-surface)] flex flex-col" data-testid={`review-card-${idx}`}>
            <div className="flex gap-1 mb-4" aria-label={`${it.stars || 5} out of 5 stars`}>
              {Array.from({ length: it.stars || 5 }).map((_, i) => <Star key={i} size={16} fill={tenant.primary_color} strokeWidth={0} />)}
            </div>
            <p className="font-cormorant italic text-xl md:text-[22px] leading-relaxed flex-1" style={{ color: "var(--l-text)" }}>&ldquo;{it.body}&rdquo;</p>
            <footer className="flex items-center gap-4 mt-6 pt-5 border-t border-[var(--l-border)]">
              {it.photo_url && (
                <img
                  src={it.photo_url}
                  alt={it.author || "Client photo"}
                  loading="lazy"
                  className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                  style={{ border: `2px solid ${tenant.primary_color}` }}
                  data-testid={`review-photo-${idx}`}
                />
              )}
              <div className="min-w-0">
                <div className="font-outfit font-semibold text-sm leading-tight" style={{ color: "var(--l-text)" }}>{it.author}</div>
                {it.role && (
                  <div className="font-outfit text-xs mt-1 leading-snug" style={{ color: "var(--l-text-sec)" }}>{it.role}</div>
                )}
                {it.company && (
                  <div className="font-outfit text-xs leading-snug" style={{ color: "var(--l-text-sec)" }}>{it.company}</div>
                )}
                {it.city && (
                  <div className="font-outfit text-[10px] font-semibold uppercase tracking-[0.15em] mt-1" style={{ color: tenant.primary_color }}>{it.city}</div>
                )}
              </div>
            </footer>
          </blockquote>
        ))}
      </div>
    </section>
  );
}
const ReviewsDef = {
  Component: ReviewsBlock, label: "Reviews (grid)",
  defaultProps: { eyebrow: "What clients say", headline: "Reviews", subheadline: "", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Subheadline", type: "textarea" },
    { key: "items", label: "Reviews", type: "items", itemSchema: [
      { key: "author", label: "Author", type: "text" },
      { key: "role", label: "Role / title", type: "text" },
      { key: "company", label: "Company", type: "text" },
      { key: "city", label: "City", type: "text" },
      { key: "photo_url", label: "Photo URL", type: "text" },
      { key: "stars", label: "Stars (1-5)", type: "text" },
      { key: "body", label: "Review", type: "textarea" },
    ]},
  ],
};

// ============================================================
// GALLERY
// ============================================================
function GalleryBlock({ props, tenant }) {
  const { eyebrow, headline, items = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="gallery">
      {eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="font-cormorant text-4xl font-semibold mb-10" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((it, idx) => (
          <img key={idx} src={it.url} alt={it.alt || ""} className="w-full h-56 object-cover rounded-md" />
        ))}
      </div>
    </section>
  );
}
const GalleryDef = {
  Component: GalleryBlock, label: "Gallery",
  defaultProps: { eyebrow: "Our work", headline: "Gallery", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "items", label: "Images", type: "items", itemSchema: [
      { key: "url", label: "Image URL", type: "url" },
      { key: "alt", label: "Alt text", type: "text" },
    ]},
  ],
};

// ============================================================
// COVERAGE AREAS
// ============================================================
function CoverageAreasBlock({ props, tenant }) {
  return (
    <section className="max-w-6xl mx-auto px-6 py-16 text-center" data-block="coverage_areas">
      {props.eyebrow && <div className="font-outfit text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{props.eyebrow}</div>}
      <h2 className="font-cormorant text-4xl font-semibold" style={{ color: "var(--l-text)" }}>{props.headline}</h2>
      <div className="flex flex-wrap justify-center gap-2 mt-8">
        {(props.items || []).map((it, i) => (
          <span key={i} className="px-4 py-2 rounded-full text-sm border" style={{ borderColor: tenant.primary_color, color: "var(--l-text)" }}>{it}</span>
        ))}
      </div>
    </section>
  );
}
const CoverageAreasDef = {
  Component: CoverageAreasBlock, label: "Coverage areas",
  defaultProps: { eyebrow: "We service", headline: "Areas we cover", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "items", label: "Cities / areas (one per line)", type: "textarea_lines" },
  ],
};

// ============================================================
// STATS
// ============================================================
function StatsBlock({ props, tenant }) {
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-block="stats">
      {(props.items || []).map((it, i) => (
        <div key={i} className="border border-[var(--l-border)] rounded-md p-6 bg-[var(--l-surface)]">
          <div className="font-cormorant text-5xl font-semibold" style={{ color: tenant.primary_color }}>{it.value}</div>
          <div className="font-outfit text-xs uppercase tracking-wider mt-2" style={{ color: "var(--l-text-sec)" }}>{it.label}</div>
        </div>
      ))}
    </section>
  );
}
const StatsDef = {
  Component: StatsBlock, label: "Statistics / counter",
  defaultProps: { items: [] },
  schema: [
    { key: "items", label: "Stats", type: "items", itemSchema: [
      { key: "value", label: "Value (e.g. 500+)", type: "text" },
      { key: "label", label: "Label", type: "text" },
    ]},
  ],
};

// ============================================================
// BEFORE / AFTER DRAG SLIDER — high-impact interactive block
// ============================================================
function BeforeAfterSliderBlock({ props, tenant }) {
  const { eyebrow, headline, before_url, after_url, before_label, after_label, caption } = props;
  const [pct, setPct] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const move = (clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setPct(x * 100);
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      move(cx);
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-block="before_after_slider">
      {eyebrow && <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
      {headline && <h2 className="font-cormorant text-4xl md:text-5xl font-semibold mb-8" style={{ color: "var(--l-text)" }}>{headline}</h2>}
      <div
        ref={containerRef}
        className="relative w-full rounded-lg overflow-hidden select-none border border-[var(--l-border)] cursor-ew-resize"
        style={{ aspectRatio: "16/9", background: "#000" }}
        onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
        onTouchStart={(e) => { dragging.current = true; move(e.touches[0].clientX); }}
        data-testid="before-after-slider"
      >
        {after_url && <img src={after_url} alt={after_label || "After"} className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />}
        {before_url && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pct}%` }}>
            <img
              src={before_url}
              alt={before_label || "Before"}
              className="h-full w-full object-cover"
              style={{ width: containerRef.current?.getBoundingClientRect().width || "100%", maxWidth: "none" }}
              draggable={false}
            />
          </div>
        )}
        <div className="absolute top-4 left-4 px-3 py-1 text-[10px] uppercase tracking-widest rounded-full text-white pointer-events-none" style={{ background: "rgba(0,0,0,0.7)" }}>{before_label || "Before"}</div>
        <div className="absolute top-4 right-4 px-3 py-1 text-[10px] uppercase tracking-widest rounded-full text-white pointer-events-none" style={{ background: tenant.primary_color, color: "#0D0D0D" }}>{after_label || "After"}</div>
        {/* Divider + handle */}
        <div className="absolute top-0 bottom-0 w-[3px] pointer-events-none" style={{ left: `${pct}%`, transform: "translateX(-50%)", background: tenant.primary_color, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }} />
        <div
          className="absolute top-1/2 w-11 h-11 rounded-full flex items-center justify-center pointer-events-none"
          style={{ left: `${pct}%`, transform: "translate(-50%, -50%)", background: tenant.primary_color, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}
        >
          <ChevronLeft size={14} color="#0D0D0D" strokeWidth={3} />
          <ChevronRight size={14} color="#0D0D0D" strokeWidth={3} />
        </div>
      </div>
      {caption && <div className="text-sm mt-4 text-center font-outfit" style={{ color: "var(--l-text-sec)" }}>{caption}</div>}
      <div className="text-xs mt-2 text-center uppercase tracking-widest font-mono-plex" style={{ color: "var(--l-text-muted, #7C7C82)" }}>Drag the slider to compare</div>
    </section>
  );
}
const BeforeAfterSliderDef = {
  Component: BeforeAfterSliderBlock, label: "Before & after slider",
  defaultProps: { eyebrow: "See the difference", headline: "Before & after", before_url: "", after_url: "", before_label: "Before", after_label: "After", caption: "" },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "before_url", label: "Before image URL", type: "url" },
    { key: "after_url", label: "After image URL", type: "url" },
    { key: "before_label", label: "Before label", type: "text" },
    { key: "after_label", label: "After label", type: "text" },
    { key: "caption", label: "Caption (optional)", type: "textarea" },
  ],
};

// ============================================================
// PROJECTS GRID — portfolio index cards for Lone Star projects
// ============================================================
function ProjectsGridBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, items = [] } = props;
  return (
    <section className="max-w-7xl mx-auto px-6 py-24" data-block="projects_grid">
      {(eyebrow || headline) && (
        <div className="mb-12 max-w-3xl">
          {eyebrow && <div className="font-mono-plex text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          {headline && <h2 className="font-cab font-black text-4xl md:text-5xl leading-tight tracking-tight" style={{ color: "var(--l-text)" }}>{headline}</h2>}
          {subheadline && <p className="font-inter mt-5 text-lg leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it, idx) => (
          <a
            key={it.href || it.title || idx}
            href={it.href || "#"}
            className="group block border rounded-lg overflow-hidden transition-all hover:-translate-y-1"
            style={{ borderColor: "var(--l-border)", background: "var(--l-surface)", boxShadow: "0 4px 12px rgba(13,13,13,.10)" }}
            data-testid={`project-card-${idx}`}
          >
            <div className="relative overflow-hidden" style={{ aspectRatio: "4/3", background: "#0D0D0D" }}>
              {it.image_url && (
                <img
                  src={it.image_url}
                  alt={it.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              )}
              <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(13,13,13,0.85), transparent)" }} />
              {it.industry && (
                <div className="absolute top-3 left-3 px-3 py-1 text-[10px] font-inter font-bold uppercase tracking-[0.2em] rounded-full" style={{ background: tenant.primary_color, color: "#0D0D0D" }}>
                  {it.industry}
                </div>
              )}
              {it.city && (
                <div className="absolute bottom-3 left-4 flex items-center gap-1.5 text-white font-mono-plex text-[11px] uppercase tracking-widest">
                  <MapPin size={12} strokeWidth={2} /> {it.city}
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="font-cab font-black text-xl leading-tight tracking-tight uppercase" style={{ color: "var(--l-text)" }}>{it.title}</div>
              {it.summary && <div className="font-inter text-sm mt-2 leading-relaxed line-clamp-2" style={{ color: "var(--l-text-sec)" }}>{it.summary}</div>}
              <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: "var(--l-border)" }}>
                <div className="flex flex-col">
                  {it.sq_ft && <span className="font-mono-plex text-[10px] uppercase tracking-widest" style={{ color: "var(--l-text-muted, #7C7C82)" }}>Sq footage</span>}
                  {it.sq_ft && <span className="font-cab font-bold text-sm" style={{ color: "var(--l-text)" }}>{it.sq_ft}</span>}
                </div>
                <span className="font-mono-plex text-[11px] uppercase tracking-widest font-bold transition-colors" style={{ color: tenant.primary_color }}>
                  View case study →
                </span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
const ProjectsGridDef = {
  Component: ProjectsGridBlock, label: "Projects grid",
  defaultProps: { eyebrow: "Case studies", headline: "Recent projects", subheadline: "", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "items", label: "Projects", type: "items", itemSchema: [
      { key: "title", label: "Project title", type: "text" },
      { key: "industry", label: "Industry badge", type: "text" },
      { key: "city", label: "City", type: "text" },
      { key: "sq_ft", label: "Sq footage", type: "text" },
      { key: "summary", label: "Short summary", type: "textarea" },
      { key: "image_url", label: "Hero image URL", type: "url" },
      { key: "href", label: "Link", type: "text" },
    ]},
  ],
};

// ============================================================
// PROJECT SPEC BAR — horizontal metric strip for case study heroes
// ============================================================
function ProjectSpecsBlock({ props, tenant }) {
  const { items = [] } = props;
  return (
    <section className="max-w-7xl mx-auto px-6 -mt-6 md:-mt-10 relative z-10" data-block="project_specs">
      <div className="rounded-lg border p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6" style={{ background: "var(--l-surface)", borderColor: "var(--l-border)", boxShadow: "0 12px 32px rgba(13,13,13,.14)" }}>
        {items.map((it, idx) => (
          <div key={it.label || idx} className="flex flex-col">
            <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-2" style={{ color: tenant.primary_color }}>{it.label}</div>
            <div className="font-cab font-black text-xl md:text-2xl leading-tight tracking-tight uppercase" style={{ color: "var(--l-text)" }}>{it.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
const ProjectSpecsDef = {
  Component: ProjectSpecsBlock, label: "Project spec bar",
  defaultProps: { items: [] },
  schema: [
    { key: "items", label: "Specs", type: "items", itemSchema: [
      { key: "label", label: "Label", type: "text" },
      { key: "value", label: "Value", type: "text" },
    ]},
  ],
};

// ============================================================
// RELATED RESOURCES — the ecosystem connector
// Every service, industry, project, and learn page ends with this
// ============================================================
function RelatedResourcesBlock({ props, tenant }) {
  const { eyebrow, headline, items = [] } = props;
  const kindStyles = {
    service:  { label: "Service",  icon: "Brush" },
    industry: { label: "Industry", icon: "Building" },
    project:  { label: "Case study", icon: "Award" },
    tool:     { label: "Tool",     icon: "ClipboardCheck" },
    article:  { label: "Guide",    icon: "Lightbulb" },
    hub:      { label: "Hub",      icon: "Layers" },
    location: { label: "Location", icon: "MapPin" },
    download: { label: "Download", icon: "Download" },
  };
  return (
    <section className="max-w-7xl mx-auto px-6 py-20" data-block="related_resources">
      <div className="mb-10 flex items-end justify-between gap-6 flex-wrap">
        <div>
          {eyebrow && <div className="font-mono-plex text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          {headline && <h2 className="font-cab font-black text-3xl md:text-4xl leading-tight tracking-tight uppercase" style={{ color: "var(--l-text)" }}>{headline}</h2>}
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
        {items.map((it, idx) => {
          const meta = kindStyles[it.kind] || kindStyles.article;
          return (
            <a
              key={it.href || idx}
              href={it.href || "#"}
              className="group block border rounded-lg p-5 transition-all hover:-translate-y-1"
              style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}
              data-testid={`related-${it.kind}-${idx}`}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${tenant.primary_color}22` }}>
                  <Icon name={meta.icon} size={16} strokeWidth={1.8} style={{ color: tenant.primary_color }} />
                </div>
                <span className="font-mono-plex text-[10px] uppercase tracking-widest font-bold" style={{ color: tenant.primary_color }}>{meta.label}</span>
              </div>
              <div className="font-cab font-black text-base leading-tight tracking-tight uppercase mb-2" style={{ color: "var(--l-text)" }}>{it.title}</div>
              {it.body && <div className="font-inter text-xs leading-relaxed line-clamp-3" style={{ color: "var(--l-text-sec)" }}>{it.body}</div>}
              <div className="font-mono-plex text-[10px] uppercase tracking-widest font-bold mt-4 transition-colors" style={{ color: tenant.primary_color }}>
                Read →
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
const RelatedResourcesDef = {
  Component: RelatedResourcesBlock, label: "Related resources",
  defaultProps: { eyebrow: "Keep exploring", headline: "Related resources", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "items", label: "Resources", type: "items", itemSchema: [
      { key: "kind", label: "Kind", type: "select", options: ["service", "industry", "project", "tool", "article", "hub", "location", "download"] },
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Short description", type: "textarea" },
      { key: "href", label: "Link", type: "text" },
    ]},
  ],
};

// ============================================================
// ARTICLE — long-form content with H2/H3, prose, callouts, tables
// Data-driven from a structured `sections` array
// ============================================================
function ArticleBlock({ props, tenant }) {
  const {
    eyebrow, headline, subheadline, reading_time, last_updated, sections = [],
    author, reviewer, commercial_experience,
    downloads, sidebar_resources, pdf_url,
  } = props;

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const handlePrint = (e) => { e.preventDefault(); window.print(); };
  const handleEmail = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`${headline} — Lone Star Striping Pros`);
    const body = encodeURIComponent(`I thought you'd find this useful — a commercial reference from Lone Star:\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const handleSave = (e) => {
    e.preventDefault();
    try {
      const key = "lsp-maintenance-binder";
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      const entry = { title: headline, path: currentPath, saved_at: new Date().toISOString() };
      if (!list.find((x) => x.path === entry.path)) list.push(entry);
      localStorage.setItem(key, JSON.stringify(list));
      const btn = e.currentTarget;
      const orig = btn.querySelector("span")?.innerText;
      if (btn.querySelector("span")) {
        btn.querySelector("span").innerText = "Saved to binder ✓";
        setTimeout(() => { if (btn.querySelector("span")) btn.querySelector("span").innerText = orig; }, 2200);
      }
    } catch {}
  };

  return (
    <section className="max-w-7xl mx-auto px-6 py-12" data-block="article">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-12">
        {/* MAIN COLUMN */}
        <article className="min-w-0">
          <header className="mb-12 pb-8 border-b" style={{ borderColor: "var(--l-border)" }}>
            {eyebrow && <div className="font-mono-plex text-xs uppercase tracking-[0.3em] mb-4" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
            {headline && <h1 className="font-cab font-black text-4xl md:text-5xl leading-[1.05] tracking-tight mb-6" style={{ color: "var(--l-text)" }}>{headline}</h1>}
            {subheadline && <p className="font-inter text-lg leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
            {/* Byline strip */}
            <div className="mt-8 pt-6 border-t grid md:grid-cols-2 gap-4" style={{ borderColor: "var(--l-border)" }}>
              {author && (
                <div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--l-text-muted, #7C7C82)" }}>Written by</div>
                  <div className="font-cab font-bold text-sm" style={{ color: "var(--l-text)" }}>{author}</div>
                </div>
              )}
              {reviewer && (
                <div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--l-text-muted, #7C7C82)" }}>Reviewed by</div>
                  <div className="font-cab font-bold text-sm" style={{ color: "var(--l-text)" }}>{reviewer}</div>
                </div>
              )}
              {commercial_experience && (
                <div className="md:col-span-2">
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--l-text-muted, #7C7C82)" }}>Commercial experience</div>
                  <div className="font-inter text-sm" style={{ color: "var(--l-text-sec)" }}>{commercial_experience}</div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-6 mt-6 font-mono-plex text-[11px] uppercase tracking-widest" style={{ color: "var(--l-text-muted, #7C7C82)" }}>
              {reading_time && <span className="flex items-center gap-1.5"><Clock size={12} strokeWidth={2} /> {reading_time}</span>}
              {last_updated && <span>Updated {last_updated}</span>}
            </div>
          </header>
          <div className="space-y-8">
            {sections.map((sec, idx) => (
              <ArticleSection key={idx} section={sec} tenant={tenant} />
            ))}
          </div>
        </article>

        {/* STICKY SIDEBAR */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-6">
            {/* Downloads card */}
            {downloads !== false && (
              <div className="border rounded-lg p-5" style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}>
                <div className="font-mono-plex text-[10px] uppercase tracking-widest font-bold mb-4" style={{ color: tenant.primary_color }}>Downloads & share</div>
                <div className="space-y-2">
                  {pdf_url && (
                    <a href={pdf_url} data-tenant-link="raw" download className="flex items-center gap-3 p-3 rounded-md border transition-colors hover:bg-opacity-10 group" style={{ borderColor: "var(--l-border)" }} data-testid="download-pdf-btn">
                      <Download size={16} strokeWidth={2} style={{ color: tenant.primary_color }} />
                      <span className="font-cab font-bold text-sm" style={{ color: "var(--l-text)" }}>Download PDF</span>
                    </a>
                  )}
                  <button onClick={handlePrint} className="w-full flex items-center gap-3 p-3 rounded-md border transition-colors hover:bg-opacity-10 group text-left" style={{ borderColor: "var(--l-border)" }} data-testid="print-btn">
                    <Icon name="Printer" size={16} strokeWidth={2} style={{ color: tenant.primary_color }} />
                    <span className="font-cab font-bold text-sm" style={{ color: "var(--l-text)" }}>Print this guide</span>
                  </button>
                  <button onClick={handleEmail} className="w-full flex items-center gap-3 p-3 rounded-md border transition-colors hover:bg-opacity-10 group text-left" style={{ borderColor: "var(--l-border)" }} data-testid="email-btn">
                    <Icon name="Mail" size={16} strokeWidth={2} style={{ color: tenant.primary_color }} />
                    <span className="font-cab font-bold text-sm" style={{ color: "var(--l-text)" }}>Email this guide</span>
                  </button>
                  <button onClick={handleSave} className="w-full flex items-center gap-3 p-3 rounded-md border transition-colors hover:bg-opacity-10 group text-left" style={{ borderColor: "var(--l-border)" }} data-testid="save-binder-btn">
                    <Icon name="Bookmark" size={16} strokeWidth={2} style={{ color: tenant.primary_color }} />
                    <span className="font-cab font-bold text-sm" style={{ color: "var(--l-text)" }}>Save to Maintenance Binder</span>
                  </button>
                </div>
              </div>
            )}
            {/* Commercial Resources quick links */}
            {sidebar_resources && sidebar_resources.length > 0 && (
              <div className="border rounded-lg p-5" style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}>
                <div className="font-mono-plex text-[10px] uppercase tracking-widest font-bold mb-4" style={{ color: tenant.primary_color }}>Commercial resources</div>
                <div className="space-y-1">
                  {sidebar_resources.map((r, i) => (
                    <a key={i} href={r.href} className="flex items-center gap-2 py-2 px-1 text-sm font-inter transition-colors hover:opacity-70" style={{ color: "var(--l-text)" }}>
                      <span style={{ color: tenant.primary_color }}>✓</span>
                      <span>{r.title}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {/* Quote CTA card */}
            <div className="border rounded-lg p-5" style={{ borderColor: tenant.primary_color, background: `${tenant.primary_color}0d` }}>
              <div className="font-mono-plex text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: tenant.primary_color }}>Ready to talk?</div>
              <div className="font-cab font-black text-lg leading-tight tracking-tight uppercase mb-3" style={{ color: "var(--l-text)" }}>Get a fixed-price commercial quote in 48 hours.</div>
              <a href="/contact" className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-cab font-bold text-sm uppercase tracking-wider transition-transform hover:scale-105" style={{ background: tenant.primary_color, color: "#0D0D0D" }}>Request estimate →</a>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
function ArticleSection({ section, tenant }) {
  const { heading, level, body, callout, list, table } = section;
  const H = level === 3 ? "h3" : "h2";
  return (
    <section>
      {heading && (
        <H
          className={
            level === 3
              ? "font-cab font-bold text-xl md:text-2xl leading-tight tracking-tight mt-4 mb-3"
              : "font-cab font-black text-2xl md:text-3xl leading-tight tracking-tight uppercase mt-2 mb-4"
          }
          style={{ color: "var(--l-text)" }}
        >
          {heading}
        </H>
      )}
      {body && (
        <div className="font-inter text-[16px] leading-[1.75] space-y-4" style={{ color: "var(--l-text-sec)" }}>
          {body.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
      {list && list.length > 0 && (
        <ul className="mt-4 space-y-2 font-inter text-[16px] leading-relaxed" style={{ color: "var(--l-text-sec)" }}>
          {list.map((it, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: tenant.primary_color }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
      {callout && (
        <div className="mt-6 border-l-4 p-5 rounded-r-md" style={{ borderColor: tenant.primary_color, background: `${tenant.primary_color}0d` }}>
          {callout.label && <div className="font-mono-plex text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: tenant.primary_color }}>{callout.label}</div>}
          <div className="font-inter text-[15px] leading-relaxed" style={{ color: "var(--l-text)" }}>{callout.body}</div>
        </div>
      )}
      {table && table.rows && table.rows.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full border-collapse text-sm font-inter">
            <thead>
              <tr>
                {(table.headers || []).map((h, i) => (
                  <th key={i} className="text-left border p-3 font-cab font-bold uppercase text-xs tracking-widest" style={{ borderColor: "var(--l-border)", background: "var(--l-surface)", color: "var(--l-text)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} className="border p-3" style={{ borderColor: "var(--l-border)", color: "var(--l-text-sec)" }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
const ArticleDef = {
  Component: ArticleBlock, label: "Article (long-form)",
  defaultProps: { eyebrow: "", headline: "", subheadline: "", reading_time: "", last_updated: "", sections: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "reading_time", label: "Reading time", type: "text" },
    { key: "last_updated", label: "Last updated", type: "text" },
  ],
};

// ============================================================
// HUB INDEX — knowledge hub landing with sub-article cards
// ============================================================
function HubIndexBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, items = [] } = props;
  return (
    <section className="max-w-6xl mx-auto px-6 py-16" data-block="hub_index">
      {(eyebrow || headline) && (
        <div className="mb-12 max-w-3xl">
          {eyebrow && <div className="font-mono-plex text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          {headline && <h2 className="font-cab font-black text-3xl md:text-4xl leading-tight tracking-tight uppercase" style={{ color: "var(--l-text)" }}>{headline}</h2>}
          {subheadline && <p className="font-inter mt-5 text-lg leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((it, idx) => (
          <a
            key={it.href || idx}
            href={it.href || "#"}
            className="group flex items-start gap-5 border rounded-lg p-6 transition-all hover:-translate-y-0.5"
            style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}
            data-testid={`hub-article-${idx}`}
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-md flex items-center justify-center font-cab font-black text-lg" style={{ background: `${tenant.primary_color}22`, color: tenant.primary_color }}>
              {String(idx + 1).padStart(2, "0")}
            </div>
            <div className="flex-1">
              <div className="font-cab font-black text-lg leading-tight tracking-tight uppercase mb-2" style={{ color: "var(--l-text)" }}>{it.title}</div>
              {it.body && <div className="font-inter text-sm leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{it.body}</div>}
              <div className="flex items-center gap-3 mt-3 font-mono-plex text-[10px] uppercase tracking-widest" style={{ color: "var(--l-text-muted, #7C7C82)" }}>
                {it.reading_time && <span>{it.reading_time}</span>}
                <span style={{ color: tenant.primary_color }}>Read →</span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
const HubIndexDef = {
  Component: HubIndexBlock, label: "Knowledge hub index",
  defaultProps: { eyebrow: "In this hub", headline: "Every resource in this hub", subheadline: "", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
    { key: "items", label: "Articles", type: "items", itemSchema: [
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Summary", type: "textarea" },
      { key: "href", label: "Link", type: "text" },
      { key: "reading_time", label: "Reading time", type: "text" },
    ]},
  ],
};

// ============================================================
// TOOLKIT GRID — Property Manager downloadable resources
// Open vs. gated PDFs, with a lead-capture flow for gated items
// ============================================================
function ToolkitGridBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, items = [] } = props;
  const [gatedDoc, setGatedDoc] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const submitGated = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/toolkit/request`, {
        doc_id: gatedDoc.id, ...form,
      });
      setDownloadUrl(r.data.download_url);
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setGatedDoc(null);
    setForm({ name: "", email: "", company: "", role: "" });
    setDownloadUrl(null);
  };

  return (
    <section className="max-w-7xl mx-auto px-6 py-20" data-block="toolkit_grid">
      {(eyebrow || headline) && (
        <div className="mb-12 max-w-3xl">
          {eyebrow && <div className="font-mono-plex text-xs uppercase tracking-[0.3em] mb-3" style={{ color: tenant.primary_color }}>{eyebrow}</div>}
          {headline && <h2 className="font-cab font-black text-3xl md:text-4xl leading-tight tracking-tight uppercase" style={{ color: "var(--l-text)" }}>{headline}</h2>}
          {subheadline && <p className="font-inter mt-5 text-lg leading-relaxed" style={{ color: "var(--l-text-sec)" }}>{subheadline}</p>}
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, idx) => {
          const isGated = it.access === "gated";
          return (
            <div
              key={it.id || idx}
              className="border rounded-lg p-6 flex flex-col group transition-all hover:-translate-y-1"
              style={{ borderColor: "var(--l-border)", background: "var(--l-surface)" }}
              data-testid={`toolkit-card-${it.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${tenant.primary_color}22` }}>
                  <Download size={20} strokeWidth={1.8} style={{ color: tenant.primary_color }} />
                </div>
                {isGated ? (
                  <span className="font-mono-plex text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border" style={{ color: tenant.primary_color, borderColor: tenant.primary_color }}>Premium</span>
                ) : (
                  <span className="font-mono-plex text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full" style={{ color: "#7C7C82", background: "var(--l-bg)" }}>Free</span>
                )}
              </div>
              <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--l-text-muted, #7C7C82)" }}>{it.eyebrow}</div>
              <div className="font-cab font-black text-lg leading-tight tracking-tight uppercase mb-2" style={{ color: "var(--l-text)" }}>{it.title}</div>
              <div className="font-inter text-sm leading-relaxed flex-1" style={{ color: "var(--l-text-sec)" }}>{it.subtitle}</div>
              {isGated ? (
                <button
                  onClick={() => setGatedDoc(it)}
                  className="mt-5 w-full py-2.5 px-4 rounded-md font-cab font-bold text-sm uppercase tracking-wider transition-transform hover:scale-[1.02]"
                  style={{ background: tenant.primary_color, color: "#0D0D0D" }}
                  data-testid={`gated-cta-${it.id}`}
                >
                  Get download →
                </button>
              ) : (
                <a
                  href={`${API}/toolkit/download/${it.id}`}
                  data-tenant-link="raw"
                  target="_blank"
                  rel="noopener"
                  className="mt-5 w-full py-2.5 px-4 rounded-md font-cab font-bold text-sm uppercase tracking-wider text-center transition-transform hover:scale-[1.02] block"
                  style={{ background: tenant.primary_color, color: "#0D0D0D" }}
                  data-testid={`open-cta-${it.id}`}
                >
                  Download PDF →
                </a>
              )}
            </div>
          );
        })}
      </div>

      {/* Lead-capture modal */}
      {gatedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(13,13,13,0.7)" }} onClick={closeModal}>
          <div className="relative max-w-md w-full rounded-lg p-8" style={{ background: "var(--l-surface)", border: "1px solid var(--l-border)" }} onClick={(e) => e.stopPropagation()}>
            <button onClick={closeModal} className="absolute top-4 right-4 text-2xl" style={{ color: "var(--l-text-muted, #7C7C82)" }}>×</button>
            {!downloadUrl ? (
              <>
                <div className="font-mono-plex text-[10px] uppercase tracking-widest mb-2" style={{ color: tenant.primary_color }}>Premium download</div>
                <h3 className="font-cab font-black text-2xl leading-tight tracking-tight uppercase mb-3" style={{ color: "var(--l-text)" }}>{gatedDoc.title}</h3>
                <p className="font-inter text-sm mb-6" style={{ color: "var(--l-text-sec)" }}>Fill out the form below and we'll send you the download immediately. No spam. Property managers only.</p>
                <form onSubmit={submitGated} className="space-y-3" data-testid="gated-form">
                  <input required type="text" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 rounded-md border font-inter text-sm" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} />
                  <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-2.5 rounded-md border font-inter text-sm" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} />
                  <input type="text" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full px-4 py-2.5 rounded-md border font-inter text-sm" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} />
                  <input type="text" placeholder="Role (e.g. Property Manager)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-2.5 rounded-md border font-inter text-sm" style={{ borderColor: "var(--l-border)", background: "var(--l-bg)", color: "var(--l-text)" }} />
                  <button type="submit" disabled={submitting} className="w-full py-3 rounded-md font-cab font-bold text-sm uppercase tracking-wider" style={{ background: tenant.primary_color, color: "#0D0D0D" }}>
                    {submitting ? "Sending..." : "Get the PDF"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: `${tenant.primary_color}22` }}>
                  <Check size={24} strokeWidth={2.5} style={{ color: tenant.primary_color }} />
                </div>
                <h3 className="font-cab font-black text-2xl uppercase mb-3" style={{ color: "var(--l-text)" }}>Ready to download</h3>
                <p className="font-inter text-sm mb-6" style={{ color: "var(--l-text-sec)" }}>Your copy of <strong>{gatedDoc.title}</strong> is ready.</p>
                <a href={`${API.replace(/\/api$/,'')}${downloadUrl}`} data-tenant-link="raw" target="_blank" rel="noopener" className="inline-block px-6 py-3 rounded-md font-cab font-bold text-sm uppercase tracking-wider" style={{ background: tenant.primary_color, color: "#0D0D0D" }} data-testid="gated-download-link">Open PDF</a>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
const ToolkitGridDef = {
  Component: ToolkitGridBlock, label: "Toolkit grid",
  defaultProps: { eyebrow: "Downloads", headline: "Property Manager Toolkit", subheadline: "", items: [] },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
  ],
};

// ============================================================
// DFW SERVICE AREA MAP (interactive)
// ============================================================
function DFWServiceMapBlock({ props, tenant }) {
  const [selected, setSelected] = useState(props.default_city || null);
  const cities = props.cities || [];

  // Convert approx lat/lng to SVG x/y within the viewBox.
  // viewBox: 0 0 1000 620
  const LNG_MIN = -97.55; const LNG_MAX = -96.30;
  const LAT_MIN = 32.35;  const LAT_MAX = 33.50;
  const project = (lat, lng) => ({
    x: ((lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * 1000,
    y: ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * 620,
  });

  const selectedCity = cities.find(c => c.slug === selected) || null;
  const brandColor = tenant?.primary_color || "#FFC107";

  return (
    <section className="w-full py-16" style={{ background: "#0A0A0A" }}>
      <div className="max-w-7xl mx-auto px-6">
        {props.eyebrow && (
          <div className="mb-3" style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "0.75rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: brandColor,
          }}>{props.eyebrow}</div>
        )}
        {props.headline && (
          <h2 className="mb-6" style={{
            fontFamily: "Cormorant Garamond, serif",
            fontSize: "clamp(2rem, 4vw, 3.5rem)",
            fontWeight: 600,
            color: "#FFF",
            lineHeight: 1.1,
          }}>{props.headline}</h2>
        )}
        {props.subheadline && (
          <p className="max-w-3xl mb-10" style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "1.1rem",
            color: "#D4D4D4",
            lineHeight: 1.6,
          }}>{props.subheadline}</p>
        )}

        <div className="grid lg:grid-cols-[1fr_400px] gap-8 items-start">
          {/* MAP */}
          <div className="relative rounded-2xl overflow-hidden" style={{
            background: "linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)",
            border: "1px solid rgba(255,193,7,0.15)",
            aspectRatio: "1000 / 620",
            minHeight: 460,
          }}>
            <svg viewBox="0 0 1000 620" className="w-full h-full" style={{ display: "block" }}>
              {/* Faint DFW outline suggesting counties */}
              <defs>
                <pattern id="dfw-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="1" />
                </pattern>
                <radialGradient id="dfw-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(255,193,7,0.12)" />
                  <stop offset="100%" stopColor="rgba(255,193,7,0)" />
                </radialGradient>
                <filter id="dfw-pin-glow">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <rect x="0" y="0" width="1000" height="620" fill="url(#dfw-grid)" />

              {/* Soft glow around HQ (Garland) */}
              {cities.filter(c => c.is_hq).map(hq => {
                const p = project(hq.lat, hq.lng);
                return <circle key="glow" cx={p.x} cy={p.y} r={140} fill="url(#dfw-glow)" />;
              })}

              {/* County-label suggestions (very faint) */}
              <text x="480" y="60" textAnchor="middle" style={{
                fontFamily: "Outfit, sans-serif", fontSize: 12, letterSpacing: "0.3em",
                fill: "rgba(255,255,255,0.15)", textTransform: "uppercase",
              }}>DENTON COUNTY</text>
              <text x="480" y="200" textAnchor="middle" style={{
                fontFamily: "Outfit, sans-serif", fontSize: 12, letterSpacing: "0.3em",
                fill: "rgba(255,255,255,0.15)", textTransform: "uppercase",
              }}>COLLIN COUNTY</text>
              <text x="200" y="450" textAnchor="middle" style={{
                fontFamily: "Outfit, sans-serif", fontSize: 12, letterSpacing: "0.3em",
                fill: "rgba(255,255,255,0.15)", textTransform: "uppercase",
              }}>TARRANT COUNTY</text>
              <text x="700" y="450" textAnchor="middle" style={{
                fontFamily: "Outfit, sans-serif", fontSize: 12, letterSpacing: "0.3em",
                fill: "rgba(255,255,255,0.15)", textTransform: "uppercase",
              }}>DALLAS COUNTY</text>
              <text x="900" y="380" textAnchor="middle" style={{
                fontFamily: "Outfit, sans-serif", fontSize: 12, letterSpacing: "0.3em",
                fill: "rgba(255,255,255,0.15)", textTransform: "uppercase",
              }}>ROCKWALL</text>

              {/* Pins */}
              {cities.map(c => {
                const p = project(c.lat, c.lng);
                const isSelected = c.slug === selected;
                const isHq = c.is_hq;
                const r = isHq ? 12 : (isSelected ? 11 : 7);
                return (
                  <g
                    key={c.slug}
                    onClick={() => setSelected(c.slug)}
                    style={{ cursor: "pointer" }}
                    filter={isSelected || isHq ? "url(#dfw-pin-glow)" : undefined}
                    data-testid={`dfw-map-pin-${c.slug}`}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={r}
                      fill={isHq ? brandColor : (isSelected ? brandColor : "rgba(255,193,7,0.55)")}
                      stroke={isSelected || isHq ? "#FFF" : "rgba(255,255,255,0.5)"}
                      strokeWidth={isSelected || isHq ? 2.5 : 1.5}
                    />
                    {isHq && (
                      <text
                        x={p.x}
                        y={p.y + 4}
                        textAnchor="middle"
                        style={{
                          fontFamily: "Outfit, sans-serif",
                          fontSize: 10,
                          fontWeight: 800,
                          fill: "#0A0A0A",
                        }}
                      >HQ</text>
                    )}
                    <text
                      x={p.x}
                      y={p.y - r - 6}
                      textAnchor="middle"
                      style={{
                        fontFamily: "Outfit, sans-serif",
                        fontSize: isHq ? 13 : (isSelected ? 12 : 10),
                        fontWeight: isHq || isSelected ? 700 : 500,
                        fill: isSelected || isHq ? "#FFF" : "rgba(255,255,255,0.7)",
                        pointerEvents: "none",
                      }}
                    >{c.name}</text>
                  </g>
                );
              })}
            </svg>

            <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full" style={{
              background: "rgba(0,0,0,0.6)",
              border: "1px solid rgba(255,193,7,0.3)",
              fontFamily: "Outfit, sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: brandColor,
            }}>
              {cities.length} DFW Cities · Click a Pin
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="rounded-2xl p-8" style={{
            background: "linear-gradient(180deg, rgba(255,193,7,0.06) 0%, rgba(0,0,0,0) 100%)",
            border: "1px solid rgba(255,193,7,0.2)",
            minHeight: 460,
          }}>
            {selectedCity ? (
              <div>
                <div style={{
                  fontFamily: "Outfit, sans-serif",
                  fontSize: "0.7rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: brandColor,
                }}>
                  {selectedCity.is_hq ? "Home Base · HQ" : `${selectedCity.county} · ${selectedCity.distance}`}
                </div>
                <h3 style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "2.25rem",
                  fontWeight: 600,
                  color: "#FFF",
                  marginTop: "0.5rem",
                  marginBottom: "0.5rem",
                }}>{selectedCity.name}</h3>
                <p style={{
                  fontFamily: "Outfit, sans-serif",
                  fontSize: "0.95rem",
                  color: "#D4D4D4",
                  lineHeight: 1.5,
                  marginBottom: "1.5rem",
                }}>Pop. {selectedCity.population}. {selectedCity.tagline}</p>

                {selectedCity.services?.length > 0 && (
                  <div className="mb-5">
                    <div style={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "0.7rem",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      color: "#B7A98B",
                      marginBottom: "0.5rem",
                    }}>Top services</div>
                    <ul className="space-y-1">
                      {selectedCity.services.map((s, i) => (
                        <li key={i}>
                          <a href={s.href} style={{
                            color: "#FFF",
                            fontFamily: "Outfit, sans-serif",
                            fontSize: "0.95rem",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}>
                            <Check size={14} style={{ color: brandColor, flexShrink: 0 }} />
                            <span>{s.title}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedCity.industries?.length > 0 && (
                  <div className="mb-5">
                    <div style={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "0.7rem",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      color: "#B7A98B",
                      marginBottom: "0.5rem",
                    }}>Industries</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedCity.industries.map((ind, i) => (
                        <a key={i} href={ind.href} style={{
                          fontSize: "0.75rem",
                          padding: "0.25rem 0.6rem",
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          color: "#FFF",
                          textDecoration: "none",
                          fontFamily: "Outfit, sans-serif",
                        }}>{ind.title}</a>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCity.projects?.length > 0 && (
                  <div className="mb-5">
                    <div style={{
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "0.7rem",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      color: "#B7A98B",
                      marginBottom: "0.5rem",
                    }}>Nearby case studies</div>
                    <ul className="space-y-1">
                      {selectedCity.projects.map((p, i) => (
                        <li key={i}>
                          <a href={p.href} style={{
                            color: "#FFF",
                            fontFamily: "Outfit, sans-serif",
                            fontSize: "0.9rem",
                            textDecoration: "underline",
                            textUnderlineOffset: 2,
                          }}>{p.title}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-col gap-2 mt-6 pt-6" style={{ borderTop: "1px solid rgba(255,193,7,0.15)" }}>
                  <a
                    href={`/locations/${selectedCity.slug}`}
                    data-testid={`dfw-map-city-page-${selectedCity.slug}`}
                    className="text-center px-5 py-3 rounded-full font-semibold"
                    style={{
                      background: brandColor,
                      color: "#0A0A0A",
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "0.85rem",
                      textDecoration: "none",
                      letterSpacing: "0.05em",
                    }}
                  >
                    View {selectedCity.name} page →
                  </a>
                  <a
                    href="/contact"
                    data-testid="dfw-map-estimate-cta"
                    className="text-center px-5 py-3 rounded-full font-semibold"
                    style={{
                      background: "transparent",
                      border: `1.5px solid ${brandColor}`,
                      color: brandColor,
                      fontFamily: "Outfit, sans-serif",
                      fontSize: "0.85rem",
                      textDecoration: "none",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Request an estimate
                  </a>
                </div>
              </div>
            ) : (
              <div style={{
                fontFamily: "Outfit, sans-serif",
                color: "#B7A98B",
                lineHeight: 1.6,
              }}>
                <div style={{
                  fontSize: "0.7rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: brandColor,
                  marginBottom: "0.75rem",
                }}>Interactive DFW Service Area</div>
                <h3 style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "2rem",
                  fontWeight: 600,
                  color: "#FFF",
                  lineHeight: 1.15,
                  marginBottom: "1rem",
                }}>Click any city.</h3>
                <p style={{ fontSize: "0.95rem" }}>
                  See the services we offer, related case studies, and city-specific resources.
                  Then request an estimate — we typically respond within 48 hours across the entire DFW metroplex.
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <span style={{
                    width: 12, height: 12, borderRadius: "50%", background: brandColor, display: "inline-block",
                  }} />
                  <span style={{ fontSize: "0.85rem" }}>HQ — Garland, TX</span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "rgba(255,193,7,0.55)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    display: "inline-block",
                  }} />
                  <span style={{ fontSize: "0.85rem" }}>Tier 1 city</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const DFWServiceMapDef = {
  Component: DFWServiceMapBlock,
  label: "DFW Service Area Map",
  defaultProps: {
    eyebrow: "Interactive · DFW Service Area",
    headline: "34 DFW cities. One HQ. Click any pin.",
    subheadline: "",
    cities: [],
    default_city: null,
  },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Sub-headline", type: "textarea" },
  ],
};


// Full-width photo band with overlay copy — used for About page team + fleet
// showcase shots. No box, no card — just an edge-to-edge cinematic photo with
// a dark-gradient scrim and text pinned to the lower-left rule-of-thirds.
function ImageHeroBandBlock({ props, tenant }) {
  const { eyebrow, headline, subheadline, image_url, alt, align } = props || {};
  const brand = "#E5C77A";
  const anchor = align === "right" ? "right" : "left";
  return (
    <section
      className="relative overflow-hidden tenant-theme-dark"
      data-block="image_hero_band"
      style={{
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        height: "clamp(360px, 52vw, 640px)",
        backgroundColor: "#0A0A0C",
      }}
    >
      {image_url && (
        <img
          src={image_url}
          alt={alt || headline || ""}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 55%" }}
          data-testid="image-hero-band-photo"
        />
      )}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            anchor === "right"
              ? "linear-gradient(270deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0) 70%)"
              : "linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0) 70%)",
        }}
      />
      <div aria-hidden className="absolute top-0 left-0 right-0 gold-hairline" />
      <div aria-hidden className="absolute bottom-0 left-0 right-0 gold-hairline" />
      <div
        className="absolute inset-0 flex items-end"
        style={{
          padding: "clamp(1.5rem, 4vw, 3.5rem)",
          justifyContent: anchor === "right" ? "flex-end" : "flex-start",
        }}
      >
        <div className="max-w-xl">
          {eyebrow && (
            <div
              className="uppercase mb-3 flex items-center gap-3"
              style={{ color: brand, fontSize: "0.72rem", letterSpacing: "0.28em", fontWeight: 700 }}
            >
              <span aria-hidden style={{ display: "inline-block", width: 32, height: 1, background: `linear-gradient(90deg, transparent, ${brand})` }} />
              {eyebrow}
            </div>
          )}
          {headline && (
            <h2
              className="font-cab font-black text-white uppercase"
              style={{
                fontSize: "clamp(1.6rem, 3.2vw, 2.75rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.005em",
                textShadow: "0 4px 22px rgba(0,0,0,0.75)",
              }}
            >
              {headline}
            </h2>
          )}
          {subheadline && (
            <p
              className="mt-3"
              style={{
                fontSize: "clamp(0.9rem, 1.05vw, 1.05rem)",
                color: "rgba(240,240,238,0.9)",
                lineHeight: 1.55,
                textShadow: "0 2px 10px rgba(0,0,0,0.75)",
              }}
            >
              {subheadline}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
const ImageHeroBandDef = {
  Component: ImageHeroBandBlock,
  label: "Image Hero Band",
  defaultProps: { eyebrow: "", headline: "", subheadline: "", image_url: "", align: "left" },
  schema: [
    { key: "eyebrow", label: "Eyebrow", type: "text" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subheadline", label: "Subheadline", type: "textarea" },
    { key: "image_url", label: "Image URL", type: "text" },
    { key: "align", label: "Text alignment (left/right)", type: "text" },
  ],
};


export const BLOCKS = {
  projects_grid: ProjectsGridDef,
  image_hero_band: ImageHeroBandDef,
  project_specs: ProjectSpecsDef,
  related_resources: RelatedResourcesDef,
  article: ArticleDef,
  hub_index: HubIndexDef,
  toolkit_grid: ToolkitGridDef,
  calculator: {
    Component: CalculatorBlock, label: "Calculator",
    defaultProps: { tool_id: "cost-calculator", eyebrow: "Tool", headline: "", subheadline: "" },
    schema: [
      { key: "tool_id", label: "Tool ID", type: "text" },
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "headline", label: "Headline", type: "text" },
      { key: "subheadline", label: "Sub-headline", type: "textarea" },
    ],
  },
  hero: HeroDef,
  services_grid: ServicesGridDef,
  why_choose_us: WhyChooseUsDef,
  process: ProcessDef,
  stats: StatsDef,
  pricing: PricingDef,
  team: TeamDef,
  testimonials_carousel: TestimonialsCarouselDef,
  reviews: ReviewsDef,
  gallery: GalleryDef,
  before_after: BeforeAfterDef,
  before_after_slider: BeforeAfterSliderDef,
  video: VideoDef,
  map: MapDef,
  trust_badges: TrustBadgesDef,
  logos_strip: LogosStripDef,
  financing_banner: FinancingBannerDef,
  coverage_areas: CoverageAreasDef,
  about_section: AboutSectionDef,
  faq: FaqDef,
  cta_banner: CtaBannerDef,
  cta_split: CtaSplitDef,
  call_now_banner: CallNowDef,
  image_text: ImageTextDef,
  rich_text: RichTextDef,
  contact_form: ContactFormDef,
  dfw_service_map: DFWServiceMapDef,
  awards_gallery: AwardsGalleryDef,
};

export function BlockRenderer({ block, tenant, site, page }) {
  if (block.visible === false) return null;
  const def = BLOCKS[block.type];
  if (!def) return <div className="p-6 border border-dashed border-red-500 text-red-500 text-sm">Unknown block type: {block.type}</div>;
  const Comp = def.Component;
  return <Comp props={{ ...def.defaultProps, ...(block.props || {}) }} tenant={tenant} site={site} page={page} />;
}
